import PedidosModal from './PedidosModal';
import React, { useState, useEffect, createContext, useContext } from 'react';
import './App.css';
import './fix-modals.css';
import './dashboard-master.css';
import './admin-theme.css';
import './dashboard-polish.css';
import './master-administration.css';
import './master-compact.css';
import './portal-responsive.css';
import './portal-layout.css';
import './master-overview.css';
import './pagamentos.css';
import Swal from 'sweetalert2';
import IntegrationDocs from './IntegrationDocs';
import MasterSoftwareHouseDetails from './MasterSoftwareHouseDetails';
import MasterOverview from './MasterOverview';
import FaturaDetalhes from './FaturaDetalhes';
import AdminIcon from './AdminIcon';
import Pagamentos from './Pagamentos';
import AcoesFatura from './AcoesFatura';
import MasterFaturamento from './MasterFaturamento';
import { formatarCompetencia, moedaBRL, textoStatusFatura, classeStatusFatura } from './faturas-utils';

// ===================== Funções Auxiliares =====================
const formatarData = (dataString) => {
  if (!dataString || dataString === '-' || dataString === null || dataString === undefined) return '-';
  
  try {
    let data;
    
    // Se já for um objeto Date
    if (dataString instanceof Date) {
      data = dataString;
    }
    // Se for timestamp numérico
    else if (!isNaN(dataString) && dataString.toString().length >= 10) {
      data = new Date(parseInt(dataString));
    }
    // Se for string de data
    else if (typeof dataString === 'string') {
      // Formato MySQL/SQL: YYYY-MM-DD HH:MM:SS
      if (dataString.includes('-') && dataString.includes(':')) {
        const dataLocal = dataString.replace(' ', 'T');
        data = new Date(dataLocal);
      }
      // Formato brasileiro: DD/MM/YYYY
      else if (dataString.includes('/')) {
        const partes = dataString.split(/[\/\s:]/);
        if (partes.length >= 3) {
          const dia = partes[0];
          const mes = partes[1];
          const ano = partes[2];
          data = new Date(`${ano}-${mes}-${dia}${partes.length > 3 ? 'T' + partes.slice(3).join(':') : ''}`);
        }
      }
      // Formato ISO ou outros
      else {
        data = new Date(dataString);
      }
    }
    
    // Verificar se a data é válida
    if (!data || isNaN(data.getTime())) {
      return dataString; // Retorna o valor original se não conseguir converter
    }
    
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  } catch (e) {
    console.error('Erro ao formatar data:', dataString, e);
    return dataString || '-'; // Retorna o valor original em caso de erro
  }
};

const formatCpfCnpj = (value) => {
  const numbers = String(value || '').replace(/\D/g, '').slice(0, 14);

  if (numbers.length <= 11) {
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  return numbers
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};


// ===================== Context & API =====================
const AppContext = createContext();

// URL da API carregada do arquivo .env, escolhendo pelo dominio aberto no navegador.
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;

  if (hostname === 'dtvenda.dtinovacao.com.br') {
    return process.env.REACT_APP_API_URL_DTINOVACAO || 'https://apidtvenda.dtinovacao.com.br/api';
  }

  if (hostname === 'dtvenda.dtloja.com.br') {
    return process.env.REACT_APP_API_URL_DTLOJA || process.env.REACT_APP_API_URL || 'https://apidtvenda.dtloja.com.br/api';
  }

  return process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
};

const API_BASE_URL = getApiBaseUrl();

const UFS_BRASIL = [
  ['AC', 'Acre'], ['AL', 'Alagoas'], ['AP', 'Amapá'], ['AM', 'Amazonas'],
  ['BA', 'Bahia'], ['CE', 'Ceará'], ['DF', 'Distrito Federal'], ['ES', 'Espírito Santo'],
  ['GO', 'Goiás'], ['MA', 'Maranhão'], ['MT', 'Mato Grosso'], ['MS', 'Mato Grosso do Sul'],
  ['MG', 'Minas Gerais'], ['PA', 'Pará'], ['PB', 'Paraíba'], ['PR', 'Paraná'],
  ['PE', 'Pernambuco'], ['PI', 'Piauí'], ['RJ', 'Rio de Janeiro'], ['RN', 'Rio Grande do Norte'],
  ['RS', 'Rio Grande do Sul'], ['RO', 'Rondônia'], ['RR', 'Roraima'], ['SC', 'Santa Catarina'],
  ['SP', 'São Paulo'], ['SE', 'Sergipe'], ['TO', 'Tocantins']
];

const useMunicipios = (uf) => {
  const [municipios, setMunicipios] = useState([]);
  const [carregandoMunicipios, setCarregandoMunicipios] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const ufSelecionada = String(uf || '').toUpperCase();

    if (!ufSelecionada) {
      setMunicipios([]);
      setCarregandoMunicipios(false);
      return () => controller.abort();
    }

    setCarregandoMunicipios(true);
    fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${ufSelecionada}/municipios?orderBy=nome`, {
      signal: controller.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error('Não foi possível carregar as cidades');
        return response.json();
      })
      .then((data) => setMunicipios(data.map((municipio) => municipio.nome)))
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Erro ao carregar cidades do IBGE:', error);
          setMunicipios([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setCarregandoMunicipios(false);
      });

    return () => controller.abort();
  }, [uf]);

  return { municipios, carregandoMunicipios };
};

console.log('Frontend - API URL:', API_BASE_URL);

const api = {
  post: async (endpoint, data) => {
   const token = localStorage.getItem('token');
   const clienteId = localStorage.getItem('clienteSelecionadoId');
   const response = await fetch(`${API_BASE_URL}${endpoint}`, {
     method: 'POST',
     headers: {
       'Content-Type': 'application/json',
       ...(token && { Authorization: `Bearer ${token}` }),
       ...(clienteId && { 'X-Cliente-Id': clienteId })
     },
     body: JSON.stringify(data)
   });
   return response.json();
  },
  get: async (endpoint) => {
    const token = localStorage.getItem('token');
    const clienteId = localStorage.getItem('clienteSelecionadoId');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }), ...(clienteId && { 'X-Cliente-Id': clienteId }) }
    });
    return response.json();
  },
  put: async (endpoint, data) => {
    const token = localStorage.getItem('token');
    const clienteId = localStorage.getItem('clienteSelecionadoId');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(clienteId && { 'X-Cliente-Id': clienteId })
      },
      body: JSON.stringify(data)
    });
    return response.json();
  },
  delete: async (endpoint) => {
    const token = localStorage.getItem('token');
    const clienteId = localStorage.getItem('clienteSelecionadoId');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers: { ...(token && { Authorization: `Bearer ${token}` }), ...(clienteId && { 'X-Cliente-Id': clienteId }) }
    });
    return response.json();
  }
};

const carregarPedidosDoArquivo = async (arquivoId) => {
  const response = await api.get(`/pedidos/${arquivoId}/vendas`);
  if (!response.success) throw new Error(response.error || 'Erro ao carregar pedido');
  if (!response.pedidos.length) {
    Swal.fire('Atenção', 'Não foi possível encontrar pedidos no JSON.', 'warning');
    return null;
  }
  return response.pedidos;
};

// ===================== Login =====================
const Login = () => {
  const { login } = useContext(AppContext);
  const [isLogin, setIsLogin] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [formData, setFormData] = useState({
    email: '', password: '', razao_social: '', nome_fantasia: '',
    cnpj: '', ie: '', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '', cep: '', whatsapp: ''
  });
  const { municipios, carregandoMunicipios } = useMunicipios(formData.uf);

  // Máscaras de formatação
  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 8);
    return numbers.replace(/(\d{5})(\d)/, '$1-$2');
  };

  const formatWhatsApp = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0,2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0,2)}) ${numbers.slice(2,7)}-${numbers.slice(7)}`;
  };

  // Buscar dados do CNPJ na Receita Federal via BrasilAPI
  const buscarCNPJ = async (cnpj) => {
    const cnpjNumbers = cnpj.replace(/\D/g, '');
    if (cnpjNumbers.length !== 14) return;

    try {
      const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjNumbers}`);
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          razao_social: data.razao_social || prev.razao_social,
          nome_fantasia: data.nome_fantasia || prev.nome_fantasia,
          endereco: data.logradouro || prev.endereco,
          numero: data.numero || prev.numero,
          complemento: data.complemento || prev.complemento,
          bairro: data.bairro || prev.bairro,
          cidade: data.municipio || prev.cidade,
          uf: data.uf || prev.uf,
          cep: data.cep ? formatCEP(data.cep) : prev.cep,
          whatsapp: data.ddd_telefone_1 ? formatWhatsApp(data.ddd_telefone_1.replace(/\D/g, '')) : prev.whatsapp
        }));
        Swal.fire({
          icon: 'success',
          title: 'CNPJ encontrado!',
          text: 'Dados preenchidos automaticamente',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (e) {
      console.log('Erro ao buscar CNPJ:', e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isLogin) {
        const response = await api.post('/login', { email: formData.email, password: formData.password });
        if (response.success) login(response.token, response.company);
        else Swal.fire('Erro', response.error, 'error');
      } else {
        const response = await api.post('/register', formData);
        if (response.success) { 
          Swal.fire('Sucesso', 'Software house cadastrada com sucesso!', 'success');
          setIsLogin(true); 
        }
        else Swal.fire('Erro', response.error, 'error');
      }
    } catch {
      Swal.fire('Erro', 'Erro na conexão', 'error');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    switch (name) {
      case 'cnpj':
        formattedValue = formatCpfCnpj(value);
        // Buscar dados quando CNPJ estiver completo
        if (formattedValue.replace(/\D/g, '').length === 14) {
          buscarCNPJ(formattedValue);
        }
        break;
      case 'cep':
        formattedValue = formatCEP(value);
        break;
      case 'whatsapp':
        formattedValue = formatWhatsApp(value);
        break;
      case 'uf':
        formattedValue = value.toUpperCase();
        setFormData({ ...formData, uf: formattedValue, cidade: '' });
        return;
      default:
        formattedValue = value;
    }

    setFormData({ ...formData, [name]: formattedValue });
  };

  const abrirAcesso = (loginMode) => {
    setIsLogin(loginMode);
    setShowAuth(true);
  };

  const authPanel = <div className="login-card">
    <button type="button" className="auth-close" onClick={() => setShowAuth(false)} aria-label="Fechar">×</button>
    <div className="login-header">
      <span className="eyebrow">PORTAL DT VENDA</span>
      <h2>{isLogin ? 'Bem-vindo de volta' : 'Conecte sua software house'}</h2>
      <p>{isLogin ? 'Acesse clientes, aplicativos, pedidos e indicadores.' : 'Crie sua estrutura e comece a integrar seus clientes.'}</p>
    </div>
    <div className="login-tabs">
      <button type="button" className={`tab ${isLogin ? 'active' : ''}`} onClick={() => setIsLogin(true)}>Entrar</button>
      <button type="button" className={`tab ${!isLogin ? 'active' : ''}`} onClick={() => setIsLogin(false)}>Cadastrar</button>
    </div>
    <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <input
              type="email" name="email" placeholder="Email" value={formData.email}
              onChange={handleChange} required
            />
          </div>
          <div className="form-group">
            <input
              type="password" name="password" placeholder="Senha" value={formData.password}
              onChange={handleChange} required
            />
          </div>
          {!isLogin && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text" name="razao_social" placeholder="Razão Social" value={formData.razao_social}
                    onChange={handleChange} required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text" name="nome_fantasia" placeholder="Nome Fantasia" value={formData.nome_fantasia}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text" name="cnpj" placeholder="CPF ou CNPJ" value={formData.cnpj}
                    onChange={handleChange}
                    inputMode="numeric" maxLength="18"
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text" name="ie" placeholder="IE" value={formData.ie}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text" name="endereco" placeholder="Endereço" value={formData.endereco}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <input type="text" name="numero" placeholder="Número" value={formData.numero} onChange={handleChange} />
                </div>
              </div>
              <div className="form-group">
                <input type="text" name="complemento" placeholder="Complemento" value={formData.complemento} onChange={handleChange} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text" name="bairro" placeholder="Bairro" value={formData.bairro}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <select name="uf" value={formData.uf} onChange={handleChange} aria-label="Estado">
                    <option value="">Selecione a UF</option>
                    {UFS_BRASIL.map(([sigla, nome]) => <option key={sigla} value={sigla}>{sigla} — {nome}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <select name="cidade" value={formData.cidade} onChange={handleChange} disabled={!formData.uf || carregandoMunicipios} aria-label="Cidade">
                    <option value="">{carregandoMunicipios ? 'Carregando cidades...' : formData.uf ? 'Selecione a cidade' : 'Selecione primeiro a UF'}</option>
                    {formData.cidade && !municipios.includes(formData.cidade) && <option value={formData.cidade}>{formData.cidade}</option>}
                    {municipios.map((municipio) => <option key={municipio} value={municipio}>{municipio}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <input
                    type="text" name="cep" placeholder="CEP" value={formData.cep}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-group">
                <input
                  type="text" name="whatsapp" placeholder="WhatsApp" value={formData.whatsapp}
                  onChange={handleChange}
                />
              </div>
            </>
          )}
          <button type="submit" className="btn-primary">
            {isLogin ? 'Entrar no portal' : 'Cadastrar software house'}
          </button>
    </form>
  </div>;

  return (
    <div className="public-page dt-landing">
      <header className="public-header">
        <a href="#inicio" className="public-brand"><span className="brand-mark">DT</span><span className="brand-name"><b>Venda</b><small>Força de vendas integrada</small></span></a>
        <nav className="landing-nav">
          <a href="#recursos">Recursos</a><a href="#integracao">Integração</a><a href="#como-funciona">Como funciona</a>
        </nav>
        <div className="public-actions">
          <button onClick={() => abrirAcesso(true)}>Entrar</button>
          <button className="public-cta" onClick={() => abrirAcesso(false)}>Começar agora</button>
        </div>
      </header>

      <main>
        <section className="public-hero" id="inicio">
          <div className="hero-grid" aria-hidden="true"></div><div className="hero-glow hero-glow-one" aria-hidden="true"></div><div className="hero-glow hero-glow-two" aria-hidden="true"></div>
          <div className="public-copy">
            <div className="hero-badge"><i></i> Integração Via Rest API para software houses</div>
            <h1>Venda mais.<br/><span>Integre tudo.</span><br/>Cresça sem limites.</h1>
            <p>Uma plataforma completa para conectar seu ERP à equipe de vendas. Gerencie clientes, aplicativos, cargas e pedidos em uma única operação.</p>
            <div className="hero-actions">
              <button className="hero-primary" onClick={() => abrirAcesso(false)}>Quero integrar meu ERP <span>→</span></button>
              <a className="hero-secondary" href="#recursos">Conhecer a plataforma</a>
            </div>
            <div className="public-benefits"><span>✓ Sem taxa por pedido</span><span>✓ Apps no Celular e na SmartPOS</span><span>✓ API documentada</span></div>
          </div>
          <div className="hero-product">
            <div className="product-window">
              <div className="window-bar"><i></i><i></i><i></i><span>DT Venda • Gestão em tempo real</span></div>
              <img src="/images/dtvenda-hero.png" alt="Visão geral do aplicativo DT Venda" />
            </div>
            <div className="float-info float-orders"><span>↗</span><div><strong>Pedidos em tempo real</strong><small>Sincronização automática</small></div></div>
            <div className="float-info float-connected"><span>✓</span><div><strong>ERP conectado</strong><small>Carga atualizada com sucesso</small></div></div>
          </div>
        </section>

        <section className="landing-stats">
          <div><strong>100%</strong><span>Integrado ao seu ERP</span></div><div><strong>24/7</strong><span>Operação disponível</span></div><div><strong>1 token</strong><span>Seguro por aplicativo</span></div><div><strong>0%</strong><span>Taxa sobre pedidos</span></div>
        </section>

        <section className="landing-features" id="recursos">
          <div className="landing-section-head"><span className="landing-tag">PLATAFORMA COMPLETA</span><h2>Tudo que sua operação precisa.<br/><em>Em um só lugar.</em></h2><p>Da implantação do cliente ao pedido importado no ERP, o DT Venda organiza toda a jornada.</p></div>
          <div className="feature-showcase">
            <article className="feature-card feature-card-main"><span className="feature-icon">▦</span><small>GESTÃO MULTIEMPRESA</small><h3>Seus clientes e aplicativos organizados.</h3><p>Cadastre cada empresa, controle suas licenças e acompanhe todos os aplicativos ativos em uma visão central.</p><div className="mini-client-list"><span><i>D</i> Distribuidora Central <b>3 apps</b></span><span><i>M</i> Mercado Modelo <b>5 apps</b></span><span><i>R</i> Restaurante Mineiro <b>2 apps</b></span></div></article>
            <article className="feature-card feature-card-teal"><span className="feature-icon">↔</span><small>INTEGRAÇÃO JSON</small><h3>Conecte seu ERP sem mudar o que já funciona.</h3><p>Envie cargas, consulte pedidos pendentes, faça o download e confirme a sincronização pela API.</p><code>POST /api/carga-dados<br/>X-API-Token: ••••••••</code></article>
            <article className="feature-card feature-card-dark"><span className="feature-icon">▥</span><small>PEDIDOS</small><h3>Pedidos prontos para importar.</h3><p>Receba dados completos e acompanhe o status até a confirmação no ERP.</p><div className="order-pulse"><i></i><span>Novo pedido #1048</span><b>agora</b></div></article>
            {/* <article className="feature-card feature-card-gold"><span className="feature-icon">⌁</span><small>INDICADORES</small><h3>Decisões com dados reais.</h3><p>Aplicativos ativos, faturamento, valores vencidos e volume operacional sempre visíveis.</p><div className="fake-chart"><i></i><i></i><i></i><i></i><i></i><i></i></div></article> */}
          </div>
        </section>

        <section className="integration-section" id="integracao">
          <div className="integration-copy"><span className="landing-tag landing-tag-dark">API DT VENDA</span><h2>Uma ponte direta entre o aplicativo e o seu ERP.</h2><p>O fluxo de integração é simples, seguro e já está documentado dentro do portal. Cada aplicativo mantém seu token exclusivo.</p><ul><li><b>01</b><span><strong>Publique a carga</strong>Clientes, produtos, preços e demais dados em JSON.</span></li><li><b>02</b><span><strong>Receba os pedidos</strong>Liste e baixe somente os pedidos ainda pendentes.</span></li><li><b>03</b><span><strong>Confirme no ERP</strong>Marque como sincronizado após gravar com sucesso.</span></li></ul><button onClick={() => abrirAcesso(false)}>Acessar documentação completa →</button></div>
          <div className="integration-code"><div className="code-title"><span><i></i><i></i><i></i></span><b>integração-pedidos.json</b></div><pre><span className="code-muted">Buscar pedidos pendentes</span>{`\n`}<span className="code-method">GET</span> /api/app/pedidos-pendentes{`\n`}<span className="code-key">X-API-Token:</span> seu_token_app{`\n\n`}<span className="code-muted">Resposta da API</span>{`\n`}{`{`}{`\n`}  <span className="code-key">"success"</span>: <span className="code-value">true</span>,{`\n`}  <span className="code-key">"total"</span>: <span className="code-number">2</span>,{`\n`}  <span className="code-key">"pedidos"</span>: [...] {`\n`}{`}`}</pre><div className="code-success">✓ Conexão estabelecida • 200 OK</div></div>
        </section>

        <section className="workflow-section" id="como-funciona">
          <div className="landing-section-head"><span className="landing-tag">SIMPLES PARA COMEÇAR</span><h2>Da configuração à primeira venda<br/>em quatro passos.</h2></div>
          <div className="workflow-grid"><article><b>01</b><span>Crie sua conta</span><p>Cadastre os dados da software house responsável pela operação.</p></article><article><b>02</b><span>Cadastre os clientes</span><p>Organize todas as empresas atendidas dentro da sua carteira.</p></article><article><b>03</b><span>Libere os aplicativos</span><p>Gere tokens individuais sem alterar o fluxo de integração.</p></article><article><b>04</b><span>Integre e acompanhe</span><p>Publique cargas, receba pedidos e acompanhe os indicadores.</p></article></div>
        </section>

        <section className="product-gallery">
          <div className="gallery-copy">
            <span className="landing-tag landing-tag-dark">NA PALMA DA MÃO</span>
            <h2>Uma experiência pensada para quem vende.</h2>
            <p>Interface rápida, segura e intuitiva para sua equipe trabalhar de onde estiver.</p>
          </div>
          <div className="gallery-track">
            <img src="/images/dtvenda-hero.png" alt="Dashboard e visão geral do DT Venda"/>
            <img src="/images/dtvenda-showcase-2.png" alt="Login seguro DT Venda"/>
            <img src="/images/dtvenda-showcase-3.png" alt="Catálogo de produtos DT Venda"/>
            <img src="/images/dtvenda-showcase-4.png" alt="Carrinho e resumo de pedidos do DT Venda"/>
            <img src="/images/dtvenda-showcase-5.png" alt="Pagamento rápido no DT Venda"/>
            <img src="/images/dtvenda-showcase-6.png" alt="Pedidos confirmados no DT Venda"/>
            <img className="gallery-wide" src="/images/dtvenda-banner.png" alt="Recursos completos do aplicativo DT Venda"/>
          </div>
        </section>

        <section className="acquirers-section">
          <span className="acq-eyebrow">COMPATIBILIDADE GARANTIDA</span>
          <h3>Roda no celular <em>e</em> nas SmartPOS das principais adquirentes.</h3>
          <p>O DT Venda opera em smartphones Android e nos terminais SmartPOS homologados pelas maiores adquirentes do Brasil — integrando PDV e força de vendas num só app.</p>
          <div className="acquirers-grid">
            {[
              {key:'bin',   ext:'png'},{key:'caixa', ext:'jpg'},{key:'cielo', ext:'svg'},
              {key:'clover',ext:'png'},{key:'getnet',ext:'jpg'},{key:'pagbank',ext:'png'},
              {key:'picpay',ext:'png'},{key:'sicredi',ext:'png'},{key:'stone', ext:'png'},
              {key:'rede',  ext:'png'},{key:'vero',  ext:'jpg'},
            ].map(a => (
              <div className="acquirer-card" key={a.key}>
                <img src={`/images/acq-${a.key}.${a.ext}`} alt={a.key} />
              </div>
            ))}
          </div>
          <p className="acq-disclaimer">Homologação sujeita ao modelo de terminal e versão de firmware de cada adquirente.</p>
        </section>

        <section className="landing-cta"><div><span>PRONTO PARA CRESCER?</span><h2>Transforme seu ERP em uma operação de vendas completa.</h2><p>Cadastre sua software house e comece a conectar seus clientes ao DT Venda.</p></div><div><button onClick={() => abrirAcesso(false)}>Começar agora <b>→</b></button><button className="cta-login" onClick={() => abrirAcesso(true)}>Já tenho uma conta</button></div></section>
      </main>

      <footer className="landing-footer"><a href="#inicio" className="public-brand"><span className="brand-mark">DT</span><span className="brand-name"><b>Venda</b><small>Gestão simples. Resultados reais.</small></span></a><p>DT Inovação em Tecnologia LTDA • Passarella Sistemas</p><div><a href="#recursos">Plataforma</a><a href="#integracao">Integração</a><button onClick={() => abrirAcesso(true)}>Entrar</button></div></footer>

      {showAuth && <div className="auth-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowAuth(false); }}><div className="auth-shell"><div className="auth-brand-panel"><span className="hero-badge"><i></i> DT VENDA</span><h2>Gestão simples.<br/><em>Resultados reais.</em></h2><p>Toda a sua operação de vendas conectada em um só lugar.</p><div><span>✓ Clientes e aplicativos</span><span>✓ Cargas e pedidos</span><span>✓ Faturamento centralizado</span></div></div>{authPanel}</div></div>}
    </div>
  );
};

// ===================== Dashboard (Master) =====================
const MasterDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [inadimplentes, setInadimplentes] = useState([]);
  const [selecionadas, setSelecionadas] = useState([]);
  const [buscaSh, setBuscaSh] = useState('');
  const [filtroSh, setFiltroSh] = useState('todos');
  const [shExpandida, setShExpandida] = useState(null);
  const [filtroAtraso, setFiltroAtraso] = useState('vencidas');
  const [buscaAtraso, setBuscaAtraso] = useState('');
  const [processandoBloqueio, setProcessandoBloqueio] = useState(false);
  const [erroDashboard, setErroDashboard] = useState('');
  const [abaDashboard, setAbaDashboard] = useState('resumo');
  const [paginaInadimplentes, setPaginaInadimplentes] = useState(1);
  const [paginaSoftwareHouses, setPaginaSoftwareHouses] = useState(1);
  const [atualizando, setAtualizando] = useState(false);
  const [empresasCarregadas, setEmpresasCarregadas] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadEmpresas();
    loadPlanos();
    loadInadimplentes();
  }, []);

  // refresh=1 ignora o cache curto da projecao no servidor.
  const loadDashboard = async (forcar = false) => {
    try {
      const response = await api.get(`/admin/dashboard${forcar ? '?refresh=1' : ''}`);
      if (!response.success) throw new Error(response.error || 'Falha ao consultar indicadores');
      setDashboard(response.dashboard);
      setErroDashboard('');
    } catch (e) {
      setErroDashboard('Não foi possível atualizar os indicadores. Tente novamente.');
      console.error('Erro ao carregar dashboard:', e);
    }
  };

  const loadEmpresas = async () => {
    try {
      const empresas = await api.get('/admin/empresas');
      if (Array.isArray(empresas)) setEmpresas(empresas);
    } catch (e) {
      console.error('Erro ao carregar empresas:', e);
    }
    setEmpresasCarregadas(true);
  };

  const loadPlanos = async () => {
    try {
      const result = await api.get('/planos');
      setPlanos(Array.isArray(result) ? result : []);
    } catch (e) {
      console.error('Erro ao carregar planos:', e);
    }
  };

  const loadInadimplentes = async () => {
    try {
      const result = await api.get('/admin/inadimplentes');
      const lista = Array.isArray(result) ? result : [];
      setInadimplentes(lista);

    } catch (e) {
      console.error('Erro ao carregar inadimplentes:', e);
    }
  };

  const atualizarTudo = () => {
    setAtualizando(true);
    Promise.all([loadDashboard(true), loadEmpresas(), loadPlanos(), loadInadimplentes()]).finally(() => setAtualizando(false));
  };

  const alternarSelecao = (id) => setSelecionadas(current => current.includes(id) ? current.filter(item => item !== id) : [...current, id]);

  const alterarBloqueioFinanceiro = async (bloquear, ids = selecionadas) => {
    if (processandoBloqueio) return;
    if (!ids.length) return Swal.fire('Atenção', 'Selecione ao menos uma Software House.', 'warning');
    const confirmacao = await Swal.fire({
      title: bloquear ? 'Bloquear acessos?' : 'Desbloquear acessos?',
      text: bloquear
        ? `${ids.length} Software House(s) selecionada(s). O portal continuará disponível. Os aplicativos e integrações terão seus acessos suspensos.`
        : `${ids.length} Software House(s) selecionada(s). Os aplicativos e as integrações serão liberados novamente. Esta ação não quita os títulos vencidos.`,
      icon: bloquear ? 'warning' : 'question', showCancelButton: true,
      confirmButtonText: bloquear ? 'Sim, bloquear' : 'Sim, desbloquear', cancelButtonText: 'Cancelar'
    });
    if (!confirmacao.isConfirmed) return;
    setProcessandoBloqueio(true);
    try {
      const result = await api.post('/admin/software-houses/bloqueio-financeiro', { ids, bloquear });
      if (!result.success) throw new Error(result.error || 'Falha ao alterar acessos');
      await Swal.fire('Concluído', result.message, 'success');
      setSelecionadas([]);
      atualizarTudo();
    } catch (error) {
      Swal.fire('Erro', error.message || 'Não foi possível alterar os acessos.', 'error');
    } finally {
      setProcessandoBloqueio(false);
    }
  };

  const alterarStatusEmpresa = async (id, status) => {
    try {
      const response = await api.put(`/admin/empresas/${id}/status`, { status });
      if (response.success) {
        Swal.fire('Sucesso', response.message, 'success');
        loadEmpresas();
        loadDashboard(true);
      } else {
        Swal.fire('Erro', response.error, 'error');
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao alterar status', 'error');
    }
  };

  const alterarPlanoEmpresa = async (id, plano_id, planoNome) => {
    try {
      const result = await Swal.fire({
        title: 'Alterar Plano',
        text: `Deseja alterar para ${planoNome}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, alterar',
        cancelButtonText: 'Cancelar'
      });

      if (result.isConfirmed) {
        const response = await api.put(`/empresas/${id}/plano`, { plano_id });
        if (response.success) {
          Swal.fire('Sucesso', 'Plano alterado com sucesso', 'success');
          loadEmpresas();
          loadDashboard(true);
        } else {
          Swal.fire('Erro', response.error, 'error');
        }
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao alterar plano', 'error');
    }
  };

  const moeda = valor => Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const itensPorPagina = 25;
  const correspondeBusca = (item, busca) => `${item.razao_social || ''} ${item.nome_fantasia || ''} ${item.email || ''} ${item.id}`.toLocaleLowerCase('pt-BR').includes(busca.toLocaleLowerCase('pt-BR'));
  const listaAtraso = filtroAtraso === 'bloqueadas'
    ? empresas.filter(item => item.bloqueio_financeiro).map(item => ({ ...item, ...inadimplentes.find(atraso => atraso.id === item.id) }))
    : inadimplentes;
  const atrasoFiltrado = listaAtraso.filter(item => correspondeBusca(item, buscaAtraso));
  const shFiltradas = empresas.filter(item => correspondeBusca(item, buscaSh) && (filtroSh === 'todos' || item.status === filtroSh));
  const totalPaginasInadimplentes = Math.max(1, Math.ceil(atrasoFiltrado.length / itensPorPagina));
  const paginaAtualInadimplentes = Math.min(paginaInadimplentes, totalPaginasInadimplentes);
  const inadimplentesPaginados = atrasoFiltrado.slice((paginaAtualInadimplentes - 1) * itensPorPagina, paginaAtualInadimplentes * itensPorPagina);
  const totalPaginasSoftwareHouses = Math.max(1, Math.ceil(shFiltradas.length / itensPorPagina));
  const paginaAtualSoftwareHouses = Math.min(paginaSoftwareHouses, totalPaginasSoftwareHouses);
  const softwareHousesPaginadas = shFiltradas.slice((paginaAtualSoftwareHouses - 1) * itensPorPagina, paginaAtualSoftwareHouses * itensPorPagina);
  const idsPaginaInadimplentes = inadimplentesPaginados.map(item => item.id);
  const paginaInadimplentesSelecionada = idsPaginaInadimplentes.length > 0 && idsPaginaInadimplentes.every(id => selecionadas.includes(id));
  const projecao = dashboard?.financeiro?.projecao_detalhada;
  const shComProjecao = (projecao?.software_houses || []).filter(item => Number(item.valor_total) > 0);

  const selecionarPaginaInadimplentes = (marcar) => {
    setSelecionadas(current => marcar
      ? Array.from(new Set([...current, ...idsPaginaInadimplentes]))
      : current.filter(id => !idsPaginaInadimplentes.includes(id)));
  };

  const Paginacao = ({ pagina, totalPaginas, totalRegistros, onChange }) => (
    <div className="dashboard-pagination">
      <span>{totalRegistros} registro(s) · 25 por página</span>
      <div>
        <button disabled={pagina <= 1} onClick={() => onChange(pagina - 1)}>Anterior</button>
        <strong>Página {pagina} de {totalPaginas}</strong>
        <button disabled={pagina >= totalPaginas} onClick={() => onChange(pagina + 1)}>Próxima</button>
      </div>
    </div>
  );

  return (
    <div className="dashboard-master-container">
      <div className="page-header"><div><span className="eyebrow">ADMINISTRAÇÃO MASTER</span><h2>Central financeira e operacional</h2><p>Acompanhe a carteira, o faturamento e os acessos da plataforma.</p></div><button className="btn-secondary" onClick={atualizarTudo} disabled={atualizando}>{atualizando ? 'Atualizando…' : 'Atualizar indicadores'}</button></div>

      {erroDashboard && <div className="master-error" role="alert">{erroDashboard}</div>}
      <nav className="master-dashboard-tabs" aria-label="Seções do dashboard">
        <button className={abaDashboard === 'resumo' ? 'active' : ''} onClick={() => setAbaDashboard('resumo')}>
          <span>Visão geral</span><small>Indicadores financeiros e operacionais</small>
        </button>
        <button className={abaDashboard === 'faturamento' ? 'active' : ''} onClick={() => setAbaDashboard('faturamento')}>
          <span>Faturamento</span><small>Fechamento mensal e faturas das SHs</small>
        </button>
        <button className={abaDashboard === 'projecao' ? 'active' : ''} onClick={() => setAbaDashboard('projecao')}>
          <span>Projeção de faturamento</span><small>Composição por Software House</small>
        </button>
        <button className={abaDashboard === 'inadimplentes' ? 'active' : ''} onClick={() => setAbaDashboard('inadimplentes')}>
          <span>SH inadimplentes</span><small>{inadimplentes.length} com título vencido</small>
        </button>
        <button className={abaDashboard === 'software-houses' ? 'active' : ''} onClick={() => setAbaDashboard('software-houses')}>
          <span>SH cadastradas</span><small>{empresas.length} registro(s)</small>
        </button>
      </nav>

      {abaDashboard === 'resumo' && (dashboard
        ? <MasterOverview dashboard={dashboard} onAbrirAba={setAbaDashboard} />
        : !erroDashboard && <p className="loading" role="status">Carregando indicadores…</p>)}

      {abaDashboard === 'faturamento' && <MasterFaturamento api={api} onAbrirAba={setAbaDashboard}
        onAtualizar={() => { loadDashboard(true); loadInadimplentes(); loadEmpresas(); }} />}

      {abaDashboard === 'projecao' && <section className="master-projection-panel">
        <div className="management-heading"><div><span className="eyebrow">PREVISÃO DE RECEITA</span><h3>Composição do faturamento por Software House</h3></div></div>
        {!projecao ? <p role="status">{dashboard || erroDashboard ? 'Projeção indisponível. Atualize os indicadores.' : 'Calculando projeção…'}</p> : <>
          <div className="projection-totals"><div><span>Total previsto</span><strong>{moeda(projecao.total)}</strong></div><div><span>SHs com valor previsto</span><strong>{projecao.quantidade_software_houses}</strong></div><div><span>Apps utilizados na competência</span><strong>{projecao.quantidade_aplicativos}</strong></div></div>
          <p className="projection-explanation">Competência de uso: {projecao.competencia?.split('-').reverse().join('/')} · Vencimento previsto: {projecao.vencimento?.split('-').reverse().join('/')}. Estimativa pela mesma apuração da cobrança mensal, considerando o uso registrado até agora e o plano atual. Inclui aplicativos inativados após o uso. A simulação considera titulares ativos com plano pago ativo e não gera cobranças.</p>
          <div className="dashboard-master-table-container"><table className="dashboard-master-table"><thead><tr><th>Software House</th><th>Plano</th><th>Apps utilizados</th><th>Valor por app</th><th>Valor previsto</th><th>Participação</th><th>Observação</th></tr></thead><tbody>
            {!shComProjecao.length && <tr><td colSpan="7">Nenhuma Software House com valor previsto nesta competência.</td></tr>}
            {shComProjecao.map(item => <tr key={item.software_house_id}><td><strong>{item.software_house}</strong><small className="table-secondary">SH #{item.software_house_id}</small></td><td>{item.plano}</td><td>{item.quantidade_aplicativos}</td><td>{moeda(item.valor_unitario)}</td><td><strong>{moeda(item.valor_total)}</strong></td><td>{projecao.total ? (item.valor_total / projecao.total * 100).toFixed(1).replace('.', ',') : '0,0'}%</td><td>{item.observacao}</td></tr>)}
          </tbody><tfoot><tr><td colSpan="4"><strong>Total da projeção</strong></td><td><strong>{moeda(projecao.total)}</strong></td><td colSpan="2">Soma de todas as SHs acima</td></tr></tfoot></table></div>
        </>}
      </section>}

      {abaDashboard === 'inadimplentes' && <section className="delinquency-panel">
        <div className="delinquency-header">
          <div><span className="eyebrow">CONTROLE DE ACESSO</span><h3>Software Houses inadimplentes</h3><p>O portal permanece acessível. O bloqueio suspende somente os APPs, o envio de cargas e a sincronização de pedidos.</p></div>
          <div className="delinquency-actions">
            <span>{selecionadas.length} selecionada(s)</span>
            <button className="btn-secondary" disabled={!selecionadas.length || processandoBloqueio} onClick={() => alterarBloqueioFinanceiro(false)}>Desbloquear</button>
            <button className="btn-danger-solid" disabled={!selecionadas.length || processandoBloqueio} onClick={() => alterarBloqueioFinanceiro(true)}>Bloquear acessos</button>
          </div>
        </div>
        <div className="master-filters">
          <input aria-label="Buscar SH inadimplente ou bloqueada" placeholder="Buscar por nome, e-mail ou ID" value={buscaAtraso} onChange={e => { setBuscaAtraso(e.target.value); setPaginaInadimplentes(1); }} />
          <select aria-label="Filtrar cobrança ou bloqueio" value={filtroAtraso} onChange={e => { setFiltroAtraso(e.target.value); setPaginaInadimplentes(1); setSelecionadas([]); }}><option value="vencidas">Com títulos vencidos</option><option value="bloqueadas">Todas as bloqueadas financeiramente</option></select>
          <button className="btn-secondary" onClick={() => setSelecionadas(atrasoFiltrado.map(item => item.id))}>Selecionar todas ({atrasoFiltrado.length})</button>
          <button className="btn-secondary" onClick={() => setSelecionadas([])}>Limpar seleção</button>
        </div>
        <div className="delinquency-summary">
          <div><strong>{inadimplentes.length}</strong><span>SHs com atraso</span></div>
          <div><strong>{moeda(inadimplentes.reduce((total, item) => total + Number(item.valor_vencido || 0), 0))}</strong><span>Total vencido</span></div>
          <div><strong>{inadimplentes.filter(item => item.bloqueio_financeiro).length}</strong><span>Bloqueadas financeiramente</span></div>
        </div>
        <div className="dashboard-master-table-container">
          <table className="dashboard-master-table delinquency-table">
            <thead><tr>
              <th><input type="checkbox" aria-label="Selecionar página" checked={paginaInadimplentesSelecionada}
                onChange={e => selecionarPaginaInadimplentes(e.target.checked)} /></th>
              <th>Software House</th><th>Plano</th><th>Títulos</th><th>Valor vencido</th><th>Em aberto</th><th>Atraso</th><th>Impacto</th><th>Acesso</th><th>Ação</th>
            </tr></thead>
            <tbody>
              {!atrasoFiltrado.length && <tr><td colSpan="10" className="empty-financial">Nenhuma Software House neste filtro.</td></tr>}
              {inadimplentesPaginados.map(item => <tr key={item.id} className={item.bloqueio_financeiro ? 'row-blocked' : ''}>
                <td><input type="checkbox" aria-label={`Selecionar ${item.nome_fantasia || item.razao_social}`} checked={selecionadas.includes(item.id)} onChange={() => alternarSelecao(item.id)} /></td>
                <td><strong>{item.nome_fantasia || item.razao_social}</strong><small className="table-secondary">{item.email}</small></td>
                <td>{item.plano_nome}</td><td>{item.titulos_vencidos || 0}</td><td className="money-danger">{moeda(item.valor_vencido)}</td><td>{moeda(item.total_em_aberto)}</td>
                <td><span className="delay-badge">{item.dias_atraso || 0} dias</span><small className="table-secondary">desde {item.vencimento_mais_antigo ? new Date(item.vencimento_mais_antigo).toLocaleDateString('pt-BR') : '-'}</small></td>
                <td>{item.clientes ?? item.clientes_total ?? 0} cliente(s)<small className="table-secondary">{item.apps_ativos} APP(s) ativo(s)</small></td>
                <td><span className={`status-badge ${item.bloqueio_financeiro ? 'bloqueado' : 'ativo'}`}>{item.bloqueio_financeiro ? 'Bloqueado' : 'Liberado'}</span></td>
                <td><button className={item.bloqueio_financeiro ? 'btn-action btn-success' : 'btn-action btn-danger'} disabled={processandoBloqueio} onClick={() => alterarBloqueioFinanceiro(!item.bloqueio_financeiro, [item.id])}>{item.bloqueio_financeiro ? 'Desbloquear' : 'Bloquear'}</button></td>
              </tr>)}
            </tbody>
          </table>
        </div>
        <Paginacao pagina={paginaAtualInadimplentes} totalPaginas={totalPaginasInadimplentes} totalRegistros={atrasoFiltrado.length} onChange={setPaginaInadimplentes} />
      </section>}

      {abaDashboard === 'software-houses' && <div className="dashboard-master-management">
        <div className="management-heading"><div><span className="eyebrow">CADASTROS</span><h3>Software Houses</h3></div><p>Clientes de produção e aplicativos cadastrados por Software House.</p></div>
        <div className="master-filters"><input aria-label="Buscar Software House" placeholder="Buscar por nome, e-mail ou ID" value={buscaSh} onChange={e => { setBuscaSh(e.target.value); setPaginaSoftwareHouses(1); }} /><select aria-label="Status da Software House" value={filtroSh} onChange={e => { setFiltroSh(e.target.value); setPaginaSoftwareHouses(1); }}><option value="todos">Todos os status</option><option value="Ativo">Ativas</option><option value="Inativo">Inativas</option><option value="Bloqueado">Bloqueadas</option></select><span>{shFiltradas.length} Software House(s)</span></div>
        <div className="dashboard-master-table-container">
          <table className="dashboard-master-table sh-management-table">
            <thead>
              <tr>
                <th>Detalhes</th>
                <th>Software House</th>
                <th>Status</th>
                <th>Plano</th>
                <th>Clientes</th>
                <th>Aplicativos</th>
                <th>Data Cadastro</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {!shFiltradas.length && <tr><td colSpan="8">{empresasCarregadas ? 'Nenhuma Software House encontrada.' : 'Carregando Software Houses…'}</td></tr>}
              {softwareHousesPaginadas.map(empresa => (
                <React.Fragment key={empresa.id}>
                <tr>
                  <td><button className="sh-expand-button" aria-label={`${shExpandida === empresa.id ? 'Recolher' : 'Ver'} aplicativos de ${empresa.razao_social}`} aria-expanded={shExpandida === empresa.id} aria-controls={`sh-details-${empresa.id}`} onClick={() => setShExpandida(current => current === empresa.id ? null : empresa.id)}>{shExpandida === empresa.id ? '−' : '+'}</button></td>
                  <td><div className="sh-company-identity"><strong>{empresa.razao_social}</strong><small className="table-secondary">#{empresa.id} · {empresa.email}</small></div></td>
                  <td>
                    <span className={`status-badge ${empresa.status.toLowerCase()}`}>
                      {empresa.status}
                    </span>
                  </td>
                  <td>
                    <select className="table-plan-select" value={empresa.plano_id || ''}
                      disabled={empresa.role === 'MASTER'}
                      onChange={(e) => {
                        const plano = planos.find(item => String(item.id) === e.target.value);
                        if (plano) alterarPlanoEmpresa(empresa.id, plano.id, plano.nome);
                      }}>
                      {planos.map(plano => <option key={plano.id} value={plano.id}>{plano.nome} - {Number(plano.valor_mensal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</option>)}
                    </select>
                  </td>
                  <td>{empresa.clientes_total || 0}<small className="table-secondary">{empresa.clientes_ativos || 0} ativos</small></td>
                  <td><div className="sh-app-counts"><span>{empresa.apps_total || 0} total</span><span className="count-active">{empresa.apps_ativos || 0} ativos</span><span title="Apps inativos">{empresa.apps_inativos || 0} inat.</span><span title="Apps lançados">{empresa.apps_lancados || 0} lanç.</span><span className="count-demo" title="Apps de homologação">{empresa.apps_homologacao || 0} homol.</span></div></td>
                  <td>{new Date(empresa.created_at).toLocaleDateString('pt-BR')}</td>
                  <td>
                    <div className="action-buttons sh-icon-actions">
                      <button 
                        className="master-icon-button icon-success"
                        title={empresa.status === 'Ativo' ? 'SH já está ativa' : 'Ativar Software House'}
                        aria-label="Ativar Software House"
                        onClick={() => alterarStatusEmpresa(empresa.id, 'Ativo')}
                        disabled={empresa.status === 'Ativo'}
                      >
                        <AdminIcon name="power"/>
                      </button>
                      <button 
                        className="master-icon-button icon-warning"
                        title={empresa.status === 'Inativo' ? 'SH já está inativa' : 'Inativar Software House'}
                        aria-label="Inativar Software House"
                        onClick={() => alterarStatusEmpresa(empresa.id, 'Inativo')}
                        disabled={empresa.status === 'Inativo'}
                      >
                        <AdminIcon name="pause"/>
                      </button>
                      <button 
                        className="master-icon-button icon-danger"
                        title={empresa.status === 'Bloqueado' ? 'SH já está bloqueada' : 'Bloquear Software House'}
                        aria-label="Bloquear Software House"
                        onClick={() => alterarStatusEmpresa(empresa.id, 'Bloqueado')}
                        disabled={empresa.status === 'Bloqueado'}
                      >
                        <AdminIcon name="lock"/>
                      </button>
                      <button 
                        className="master-icon-button icon-info"
                        aria-label={`Mudar para ${empresa.plano_tipo === 'FREE' ? 'Plano Mensal' : 'Plano Free'}`}
                        onClick={() => alterarPlanoEmpresa(empresa.id, empresa.plano_tipo === 'FREE' ? empresa.mensal_plan_id : empresa.free_plan_id, empresa.plano_tipo === 'FREE' ? 'Plano Mensal' : 'Plano Free')}
                        title={`Mudar para ${empresa.plano_tipo === 'FREE' ? 'Plano Mensal' : 'Plano Free'}`}
                        disabled={empresa.role === 'MASTER'}
                      >
                        <AdminIcon name="switch"/>
                      </button>
                    </div>
                  </td>
                </tr>
                {shExpandida === empresa.id && <tr className="sh-details-row" id={`sh-details-${empresa.id}`}><td colSpan="8"><MasterSoftwareHouseDetails company={empresa} api={api}/></td></tr>}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao pagina={paginaAtualSoftwareHouses} totalPaginas={totalPaginasSoftwareHouses} totalRegistros={shFiltradas.length} onChange={setPaginaSoftwareHouses} />
      </div>}
    </div>
  );
};

// ===================== Planos (Master) =====================
const planoVazio = { nome: '', descricao: '', valor_mensal: '0.00', tipo: 'MENSAL', ativo: true, padrao: false };

const PlanosAdmin = () => {
  const [planos, setPlanos] = useState([]);
  const [form, setForm] = useState(planoVazio);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const carregar = async () => {
    const result = await api.get('/admin/planos');
    setPlanos(Array.isArray(result) ? result : []);
  };
  useEffect(() => { carregar(); }, []);

  const abrir = (plano = null) => {
    setEditing(plano?.id || null);
    setForm(plano ? { ...plano, ativo: Boolean(plano.ativo), padrao: Boolean(plano.padrao) } : planoVazio);
    setShowForm(true);
  };

  const salvar = async (e) => {
    e.preventDefault();
    const payload = { ...form, valor_mensal: Number(form.valor_mensal) };
    const result = editing ? await api.put(`/admin/planos/${editing}`, payload) : await api.post('/admin/planos', payload);
    if (result.success) {
      Swal.fire('Sucesso', result.message, 'success');
      setShowForm(false);
      carregar();
    } else Swal.fire('Erro', result.error || 'Nao foi possivel salvar o plano', 'error');
  };

  return <div className="plans-admin-page">
    <div className="page-header"><div><span className="eyebrow">FATURAMENTO</span><h2>Planos de assinatura</h2><p>Defina o valor mensal cobrado por aplicativo ativo e utilizado.</p></div><button className="btn-primary" onClick={() => abrir()}>+ Novo plano</button></div>
    <div className="clients-table-wrapper"><table className="data-table"><thead><tr><th>Plano</th><th>Tipo</th><th>Valor / app</th><th>Padrao</th><th>Status</th><th>Acoes</th></tr></thead><tbody>
      {planos.map(plano => <tr key={plano.id}><td><strong>{plano.nome}</strong><small className="table-secondary">{plano.descricao}</small></td><td>{plano.tipo}</td><td>{Number(plano.valor_mensal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td><td>{plano.padrao ? 'Sim' : '-'}</td><td><span className={`status-badge ${plano.ativo ? 'ativo' : 'inativo'}`}>{plano.ativo ? 'Ativo' : 'Inativo'}</span></td><td><button className="btn-action btn-info" onClick={() => abrir(plano)}>Editar</button></td></tr>)}
    </tbody></table></div>
    {showForm && <div className="modal-overlay"><div className="modal-content plan-modal"><div className="modal-header"><div><span className="modal-eyebrow">PLANO DE ASSINATURA</span><h3>{editing ? 'Editar plano' : 'Novo plano'}</h3><p>Configure o valor cobrado por aplicativo ativo e utilizado.</p></div><button type="button" className="modal-close" aria-label="Fechar" onClick={() => setShowForm(false)}>×</button></div><form onSubmit={salvar} className="modal-form">
      <div className="form-group"><label>Nome</label><input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} required /></div>
      <div className="form-group"><label>Descrição</label><textarea value={form.descricao || ''} onChange={e => setForm({ ...form, descricao: e.target.value })} /></div>
      <div className="form-row"><div className="form-group"><label>Tipo</label><select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value, valor_mensal: e.target.value === 'FREE' ? 0 : form.valor_mensal, padrao: e.target.value === 'FREE' ? false : form.padrao })}><option value="FREE">Free</option><option value="MENSAL">Mensal</option></select></div><div className="form-group"><label>Valor mensal por app</label><input type="number" min="0" step="0.01" disabled={form.tipo === 'FREE'} value={form.valor_mensal} onChange={e => setForm({ ...form, valor_mensal: e.target.value })} required /></div></div>
      <div className="form-checks"><label><input type="checkbox" checked={form.ativo} onChange={e => setForm({ ...form, ativo: e.target.checked })} /><span><strong>Plano ativo</strong><small>Disponível para vincular às Software Houses</small></span></label>{form.padrao && <label><input type="checkbox" disabled checked /><span><strong>Plano padrão</strong><small>Aplicado automaticamente em novos cadastros</small></span></label>}</div>
      <div className="modal-actions"><button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button><button type="submit" className="btn-primary">Salvar</button></div>
    </form></div></div>}
  </div>;
};

// ===================== Clientes =====================
const clienteVazio = {
  razao_social: '', nome_fantasia: '', cnpj: '', ie: '', endereco: '', numero: '',
  complemento: '', bairro: '', cidade: '', uf: '', cep: '', telefone: '', whatsapp: '', status: 'Ativo',
  token_api_dfe: '', email_habilitado: false, smtp_host: '', smtp_porta: 587,
  smtp_usuario: '', smtp_senha: '', smtp_remetente_email: '', smtp_remetente_nome: '',
  smtp_ssl: false, smtp_tls: true, logo_base64: null
};

const Clientes = ({ onOpenApps }) => {
  const { acessoLimitado } = useContext(AppContext);
  const [clientes, setClientes] = useState([]);
  const [form, setForm] = useState(clienteVazio);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [abaForm, setAbaForm] = useState('dados');

  const carregar = async () => {
    const result = await api.get('/clientes');
    setClientes(Array.isArray(result) ? result : []);
  };

  useEffect(() => { carregar(); }, []);

  const salvar = async (e) => {
    e.preventDefault();
    const result = editing ? await api.put(`/clientes/${editing}`, form) : await api.post('/clientes', form);
    if (result.success) {
      Swal.fire('Sucesso', result.message, 'success');
      setShowForm(false); setEditing(null); setForm(clienteVazio); carregar();
    } else Swal.fire('Erro', result.error, 'error');
  };

  const editar = (cliente) => {
    setForm({ ...clienteVazio, ...cliente });
    setEditing(cliente.id);
    setAbaForm('dados');
    setShowForm(true);
  };

  const anexarLogotipo = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Swal.fire('Atenção', 'Selecione um arquivo de imagem.', 'warning');
      event.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm(current => ({ ...current, logo_base64: reader.result }));
    reader.onerror = () => Swal.fire('Erro', 'Não foi possível ler a imagem.', 'error');
    reader.readAsDataURL(file);
  };

  const testarEmail = async () => {
    if (!editing) return Swal.fire('Atenção', 'Salve o cliente antes de testar o email.', 'warning');
    const result = await api.post(`/clientes/${editing}/testar-email`, form);
    if (result.success) Swal.fire('Sucesso', result.message, 'success');
    else Swal.fire('Erro', result.error || 'Falha ao testar email', 'error');
  };

  const abrirApps = (cliente) => {
    localStorage.setItem('clienteSelecionadoId', cliente.id);
    localStorage.setItem('clienteSelecionadoNome', cliente.nome_fantasia || cliente.razao_social);
    onOpenApps();
  };

  return <div className="page-container clients-page">
    <div className="page-header">
      <div><span className="eyebrow">CARTEIRA</span><h2>Clientes</h2><p>Empresas atendidas pela sua software house.</p></div>
      {!acessoLimitado && <button className="btn-primary" onClick={() => { setForm(clienteVazio); setEditing(null); setAbaForm('dados'); setShowForm(true); }}>+ Novo cliente</button>}
    </div>
    <div className="clients-table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Nome / Fantasia</th>
            <th className="col-mobile-hide">CPF/CNPJ</th>
            <th className="col-mobile-hide">Cidade / UF</th>
            <th className="col-mobile-hide">WhatsApp</th>
            <th>Apps</th>
            <th>Status</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {clientes.length === 0 && (
            <tr><td colSpan="8" style={{textAlign:'center',padding:'36px',color:'#94a3b8'}}>Nenhum cliente cadastrado</td></tr>
          )}
          {clientes.map((cliente, idx) => (
            <tr key={cliente.id}>
              <td style={{color:'#94a3b8',fontWeight:500}}>{idx + 1}</td>
              <td>
                <div style={{fontWeight:600,color:'#1e293b'}}>{cliente.nome_fantasia || cliente.razao_social}</div>
                {cliente.nome_fantasia && <div style={{fontSize:'.72rem',color:'#94a3b8',marginTop:2}}>{cliente.razao_social}</div>}
                {!!cliente.is_homologacao && <span className="demo-badge">Ambiente de homologação</span>}
              </td>
              <td style={{fontFamily:'monospace',fontSize:'.75rem'}} className="col-mobile-hide">{cliente.cnpj || '—'}</td>
              <td className="col-mobile-hide">{cliente.cidade ? `${cliente.cidade}${cliente.uf ? ' / '+cliente.uf : ''}` : '—'}</td>
              <td className="col-mobile-hide">{cliente.whatsapp || '—'}</td>
              <td>
                <span style={{fontWeight:700,color:'#1e293b'}}>{cliente.aplicativos_ativos || 0}</span>
                <span style={{color:'#94a3b8',fontSize:'.7rem'}}> / {cliente.aplicativos_total || 0}</span>
              </td>
              <td><span className={`status-badge ${(cliente.status||'').toLowerCase()}`}>{cliente.status}</span></td>
              <td>
                <div className="action-buttons">
                  <button className="ib ib-blue" title="Gerenciar aplicativos" onClick={() => abrirApps(cliente)}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    Apps
                  </button>
                  {!cliente.is_homologacao && !acessoLimitado && <button className="ib ib-ghost" title="Editar cliente" onClick={() => editar(cliente)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Editar
                  </button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {showForm && <div className="modal-overlay"><div className="modal client-modal">
      <div className="modal-title"><div><span className="eyebrow">DADOS CADASTRAIS</span><h3>{editing ? 'Editar cliente' : 'Novo cliente'}</h3></div><button type="button" onClick={() => setShowForm(false)}>×</button></div>
      <form onSubmit={salvar} className="login-form client-form">
        <div className="client-form-tabs"><button type="button" className={abaForm === 'dados' ? 'active' : ''} onClick={() => setAbaForm('dados')}>Dados</button><button type="button" className={abaForm === 'email' ? 'active' : ''} onClick={() => setAbaForm('email')}>Email</button></div>
        {abaForm === 'dados' && <>
        <div className="client-logo-editor">
          <div className="client-logo-preview">{form.logo_base64 ? <img src={form.logo_base64} alt="Prévia do logotipo do cliente" /> : <span>Sem logotipo<br />Usando o logotipo do perfil</span>}</div>
          <div className="client-logo-controls">
            <label htmlFor="client-logo-upload">Logotipo do cliente</label>
            <p>Este logotipo será exibido no aplicativo. Sem imagem, será utilizado o logotipo do perfil.</p>
            <input id="client-logo-upload" key={form.logo_base64 ? 'with-logo' : 'no-logo'} type="file" accept="image/*" onChange={anexarLogotipo} />
            {form.logo_base64 && <button type="button" className="btn-secondary" onClick={() => setForm(current => ({ ...current, logo_base64: null }))}>Remover logotipo</button>}
          </div>
        </div>
        <div className="form-row"><input required placeholder="Razão social" value={form.razao_social} onChange={e => setForm({...form, razao_social:e.target.value})}/><input placeholder="Nome fantasia" value={form.nome_fantasia} onChange={e => setForm({...form, nome_fantasia:e.target.value})}/></div>
        <div className="form-row"><input required placeholder="CPF ou CNPJ" inputMode="numeric" maxLength="18" value={form.cnpj} onChange={e => setForm({...form, cnpj:formatCpfCnpj(e.target.value)})}/><input placeholder="Inscrição estadual" value={form.ie} onChange={e => setForm({...form, ie:e.target.value})}/></div>
        <div className="form-row address-row"><input placeholder="Endereço" value={form.endereco} onChange={e => setForm({...form, endereco:e.target.value})}/><input placeholder="Número" value={form.numero} onChange={e => setForm({...form, numero:e.target.value})}/></div>
        <div className="form-row"><input placeholder="Complemento" value={form.complemento} onChange={e => setForm({...form, complemento:e.target.value})}/><input placeholder="Bairro" value={form.bairro} onChange={e => setForm({...form, bairro:e.target.value})}/></div>
        <div className="form-row"><input placeholder="Cidade" value={form.cidade} onChange={e => setForm({...form, cidade:e.target.value})}/><input placeholder="UF" maxLength="2" value={form.uf} onChange={e => setForm({...form, uf:e.target.value.toUpperCase()})}/></div>
        <div className="form-row"><input placeholder="CEP" value={form.cep} onChange={e => setForm({...form, cep:e.target.value})}/><input placeholder="Telefone / WhatsApp" value={form.whatsapp} onChange={e => setForm({...form, whatsapp:e.target.value})}/></div>
        <input placeholder="Token API DTDFEWeb" autoComplete="off" value={form.token_api_dfe || ''} onChange={e => setForm({...form, token_api_dfe:e.target.value.trim()})}/>
        {editing && <select value={form.status} onChange={e => setForm({...form, status:e.target.value})}><option>Ativo</option><option>Inativo</option><option>Bloqueado</option></select>}
        </>}
        {abaForm === 'email' && <div className="email-config-form">
          <label className="check-line"><input type="checkbox" checked={!!form.email_habilitado} onChange={e => setForm({...form,email_habilitado:e.target.checked})}/> Habilitar envio de email pelo aplicativo</label>
          <div className="form-row address-row"><input placeholder="Servidor SMTP" value={form.smtp_host || ''} onChange={e => setForm({...form,smtp_host:e.target.value})}/><input type="number" min="1" max="65535" placeholder="Porta" value={form.smtp_porta || ''} onChange={e => setForm({...form,smtp_porta:Number(e.target.value)})}/></div>
          <div className="form-row"><input placeholder="Usuário SMTP" autoComplete="off" value={form.smtp_usuario || ''} onChange={e => setForm({...form,smtp_usuario:e.target.value})}/><input type="password" placeholder="Senha SMTP" autoComplete="new-password" value={form.smtp_senha || ''} onChange={e => setForm({...form,smtp_senha:e.target.value})}/></div>
          <div className="form-row"><input type="email" placeholder="Email remetente" value={form.smtp_remetente_email || ''} onChange={e => setForm({...form,smtp_remetente_email:e.target.value})}/><input placeholder="Nome do remetente" value={form.smtp_remetente_nome || ''} onChange={e => setForm({...form,smtp_remetente_nome:e.target.value})}/></div>
          <div className="email-security"><label className="check-line"><input type="checkbox" checked={!!form.smtp_tls} onChange={e => setForm({...form,smtp_tls:e.target.checked})}/> TLS/STARTTLS</label><label className="check-line"><input type="checkbox" checked={!!form.smtp_ssl} onChange={e => setForm({...form,smtp_ssl:e.target.checked})}/> SSL implícito</label></div>
          <button className="btn-secondary test-email-button" type="button" onClick={testarEmail}>Testar email</button>
        </div>}
        <div className="modal-buttons"><button className="btn-primary" type="submit">Salvar cliente</button><button className="btn-secondary" type="button" onClick={() => setShowForm(false)}>Cancelar</button></div>
      </form>
    </div></div>}
  </div>;
};

// ===================== Integração =====================
const Integracao = () => {
  const { user } = useContext(AppContext);
  const [info, setInfo] = useState(null);
  useEffect(() => { api.get('/integracao').then(setInfo); }, []);
  const base = info?.api_base_url || 'https://apidtvenda.dtloja.com.br/api';
  const copiar = (valor) => { navigator.clipboard.writeText(valor); Swal.fire({icon:'success', title:'Copiado', timer:1200, showConfirmButton:false}); };
  return <div className="page-container integration-page">
    <div className="page-header"><div><span className="eyebrow">API DT VENDA</span><h2>Integração</h2><p>Manual completo para publicar dados no aplicativo e importar o retorno no ERP.</p><div className="integration-base"><span>URL base da API: </span><strong>{base}</strong></div></div></div>
    <IntegrationDocs base={base} info={info} onCopy={copiar} showAdministrativeToken={user?.role === 'MASTER'}/>
  </div>;
};

// ===================== Aplicativos =====================
const Aplicativos = () => {
  const { user, acessoLimitado } = useContext(AppContext);
  const isMaster = user?.role === 'MASTER';
  const [aplicativos, setAplicativos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(localStorage.getItem('clienteSelecionadoId') || '');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ 
    nome: '',
    modo_aplicacao: 1,
    modelo_impressao_pdv: 1,
    imprimir_ficha_por_quantidade: false,
    enviar_carga_cada_pedido: false,
    url_base: 'http://localhost:9002',
    qtde_mesas: 50,
    nfce: false,
    nfe: false,
    nao_sair_pesquisa: false,
    consumidor_final_padrao: false,
    habilita_mesas: false
  });
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [openEditOnDetails, setOpenEditOnDetails] = useState(false);
  const clienteSelecionadoAtual = clientes.find(c => String(c.id) === String(clienteSelecionado));
  const clienteSelecionadoEhHomologacao = !!clienteSelecionadoAtual?.is_homologacao;

  useEffect(() => {
    const iniciar = async () => {
      if (user?.tipo === 'software_house') {
        const lista = await api.get('/clientes');
        const clientesValidos = Array.isArray(lista) ? lista : [];
        setClientes(clientesValidos);
        let atual = localStorage.getItem('clienteSelecionadoId');
        if (!atual && clientesValidos.length) {
          atual = String(clientesValidos[0].id);
          localStorage.setItem('clienteSelecionadoId', atual);
          localStorage.setItem('clienteSelecionadoNome', clientesValidos[0].nome_fantasia || clientesValidos[0].razao_social);
          setClienteSelecionado(atual);
        }
      }
      loadAplicativos();
    };
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selecionarCliente = (id) => {
    const cliente = clientes.find(c => String(c.id) === String(id));
    setClienteSelecionado(id);
    if (id) {
      localStorage.setItem('clienteSelecionadoId', id);
      localStorage.setItem('clienteSelecionadoNome', cliente?.nome_fantasia || cliente?.razao_social || '');
    } else {
      localStorage.removeItem('clienteSelecionadoId');
      localStorage.removeItem('clienteSelecionadoNome');
    }
    setTimeout(loadAplicativos, 0);
  };

  const loadAplicativos = async () => {
    try {
      const apps = await api.get('/aplicativos');
      setAplicativos(apps);
    } catch (e) {
      console.error('Erro ao carregar aplicativos:', e);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/aplicativos', formData);
      if (response.success) {
        Swal.fire('Sucesso', 'Aplicativo cadastrado com sucesso!', 'success');
        setFormData({ 
          nome: '',
          modo_aplicacao: 1,
          modelo_impressao_pdv: 1,
          imprimir_ficha_por_quantidade: false,
          enviar_carga_cada_pedido: false,
          url_base: 'http://localhost:9002',
          qtde_mesas: 50,
          nfce: false,
          nfe: false,
          nao_sair_pesquisa: false,
          consumidor_final_padrao: false,
          habilita_mesas: false
        });
        setShowForm(false);
        loadAplicativos();
      } else {
        Swal.fire('Erro', response.error, 'error');
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao criar aplicativo', 'error');
    }
  };

  const ativarAplicativo = async (id) => {
    try {
      const response = await api.post(`/aplicativos/${id}/ativar`);
      if (response.success) {
        Swal.fire('Sucesso', 'Aplicativo ativado!', 'success');
        loadAplicativos();
      } else {
        Swal.fire('Erro', response.error, 'error');
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao ativar aplicativo', 'error');
    }
  };

  const inativarAplicativo = async (id) => {
    try {
      const response = await api.post(`/aplicativos/${id}/inativar`);
      if (response.success) {
        Swal.fire('Sucesso', 'Aplicativo inativado!', 'success');
        loadAplicativos();
      } else {
        Swal.fire('Erro', response.error, 'error');
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao inativar aplicativo', 'error');
    }
  };

  const desvincularAplicativo = async (id, deviceInfo) => {
    const result = await Swal.fire({
      title: 'Liberar Licença?',
      html: `<p>Isso irá liberar a licença para uso em outro aparelho.</p>
             ${deviceInfo ? `<p><small>Dispositivo atual: ${deviceInfo}</small></p>` : ''}
             <p><strong>O aplicativo voltará ao status "Lançado".</strong></p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, liberar!',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc3545'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.post(`/aplicativos/${id}/desvincular`);
        if (response.success) {
          Swal.fire('Sucesso', response.message, 'success');
          loadAplicativos();
        } else {
          Swal.fire('Erro', response.error, 'error');
        }
      } catch (e) {
        Swal.fire('Erro', 'Erro ao desvincular dispositivo', 'error');
      }
    }
  };

  const excluirAplicativo = async (id) => {
    const result = await Swal.fire({
      title: 'Tem certeza?',
      text: 'Esta ação não pode ser desfeita!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir!',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.delete(`/aplicativos/${id}`);
        if (response.success) {
          Swal.fire('Sucesso', 'Aplicativo excluído!', 'success');
          loadAplicativos();
        } else {
          Swal.fire('Erro', response.error, 'error');
        }
      } catch (e) {
        Swal.fire('Erro', 'Erro ao excluir aplicativo', 'error');
      }
    }
  };

  const verDetalhes = (app) => {
    setSelectedApp(app);
    setOpenEditOnDetails(false);
    setShowDetails(true);
  };

  const editarAplicativo = (app) => {
    setSelectedApp(app);
    setOpenEditOnDetails(true);
    setShowDetails(true);
  };

  if (loading) return <div className="loading">Carregando aplicativos...</div>;
  
  if (showDetails && selectedApp) {
    return <DetalhesAplicativo app={selectedApp} startEditing={openEditOnDetails} onBack={() => {
      setShowDetails(false);
      setSelectedApp(null);
      setOpenEditOnDetails(false);
      loadAplicativos();
    }} />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div><span className="eyebrow">LICENÇAS</span><h2>Aplicativos</h2>
          {user?.tipo === 'software_house' && <select className="client-selector" value={clienteSelecionado} onChange={e => selecionarCliente(e.target.value)}>
            <option value="" disabled>Selecione um cliente</option>{clientes.map(c => <option value={c.id} key={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>}
        </div>
        {!acessoLimitado && <button className="btn-primary" title={clienteSelecionadoEhHomologacao ? 'O cliente de homologação já possui seu aplicativo de demonstração' : ''} disabled={(user?.tipo === 'software_house' && !clienteSelecionado) || clienteSelecionadoEhHomologacao} onClick={() => setShowForm(true)}>
          Novo Aplicativo
        </button>}
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="modal app-config-modal">
            <h3>Novo Aplicativo</h3>
            <form onSubmit={handleSubmit} className="login-form">
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Nome do Aplicativo"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                />
              </div>
              
              <h4 style={{marginTop: '20px', marginBottom: '10px', fontSize: '16px'}}>Configurações do Aplicativo</h4>
              
              <div className="form-group">
                <label>Modo de Aplicação</label>
                <select
                  value={formData.modo_aplicacao}
                  onChange={(e) => setFormData({ ...formData, modo_aplicacao: parseInt(e.target.value) })}
                  style={{width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd'}}
                >
                  <option value={1}>1 - Força de vendas</option>
                  <option value={2}>2 - PDV</option>
                  {isMaster && <option value={3}>3 - PDV API</option>}
                  {isMaster && <option value={4}>4 - SmartTEF</option>}
                </select>
              </div>
              
              {Number(formData.modo_aplicacao) === 2 && (
                <>
                  <div className="form-group">
                    <label htmlFor="novo-modelo-impressao-pdv">Modelo de impressão do PDV</label>
                    <select
                      id="novo-modelo-impressao-pdv"
                      value={formData.modelo_impressao_pdv}
                      onChange={(e) => setFormData({ ...formData, modelo_impressao_pdv: Number(e.target.value) })}
                      style={{width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd'}}
                    >
                      <option value={1}>1 - Padrão</option>
                      <option value={2}>2 - Ficha</option>
                    </select>
                  </div>
                  {Number(formData.modelo_impressao_pdv) === 2 && (
                    <label className="app-config-check">
                      <input
                        type="checkbox"
                        checked={formData.imprimir_ficha_por_quantidade}
                        onChange={(e) => setFormData({ ...formData, imprimir_ficha_por_quantidade: e.target.checked })}
                      />
                      <span>Imprimir uma ficha para cada unidade do item</span>
                    </label>
                  )}
                </>
              )}
              {[1, 2].includes(Number(formData.modo_aplicacao)) && (
                <label className="app-config-check">
                  <input
                    type="checkbox"
                    checked={formData.enviar_carga_cada_pedido}
                    onChange={(e) => setFormData({ ...formData, enviar_carga_cada_pedido: e.target.checked })}
                  />
                  <span>Envio de carga a cada pedido</span>
                </label>
              )}

              <div className="form-group">
                <label>URL Base</label>
                <input
                  type="text"
                  placeholder="URL Base"
                  value={formData.url_base}
                  onChange={(e) => setFormData({ ...formData, url_base: e.target.value })}
                />
              </div>
              
              <div className="form-group">
                <label>Qtde Mesas</label>
                <input
                  type="number"
                  placeholder="Quantidade de Mesas"
                  value={formData.qtde_mesas}
                  onChange={(e) => setFormData({ ...formData, qtde_mesas: parseInt(e.target.value) })}
                />
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={formData.nfce}
                    onChange={(e) => setFormData({ ...formData, nfce: e.target.checked })}
                  />
                  <span>NFC-e</span>
                </label>
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={formData.nfe}
                    onChange={(e) => setFormData({ ...formData, nfe: e.target.checked })}
                  />
                  <span>NF-e</span>
                </label>
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={formData.nao_sair_pesquisa}
                    onChange={(e) => setFormData({ ...formData, nao_sair_pesquisa: e.target.checked })}
                  />
                  <span>Não sair da tela de pesquisa</span>
                </label>
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={formData.consumidor_final_padrao}
                    onChange={(e) => setFormData({ ...formData, consumidor_final_padrao: e.target.checked })}
                  />
                  <span>Consumidor final como padrão</span>
                </label>
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={formData.habilita_mesas}
                    onChange={(e) => setFormData({ ...formData, habilita_mesas: e.target.checked })}
                  />
                  <span>Habilita controle de mesas</span>
                </label>
              </div>
              
              <div className="modal-buttons">
                <button type="submit" className="btn-primary">Salvar</button>
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              {user?.tipo === 'software_house' && !clienteSelecionado && <th>Cliente</th>}
              <th>Nome</th>
              <th className="col-mobile-hide">Token</th>
              <th>Status</th>
              <th>Integradora</th>
              <th>Vinculação</th>
              <th className="col-mobile-hide">Data Criação</th>
              <th className="col-mobile-hide">Data Ativação</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {aplicativos.map(app => (
              <tr key={app.id}>
                {user?.tipo === 'software_house' && !clienteSelecionado && <td>{app.cliente_nome_fantasia || app.cliente_razao_social}</td>}
                <td>{app.nome}{!!app.is_homologacao && <span className="demo-badge app-demo-badge">Demonstração</span>}</td>
                <td className="col-mobile-hide">
                  <code 
                    className="token" 
                    onClick={() => {
                      navigator.clipboard.writeText(app.token);
                      Swal.fire({
                        icon: 'success',
                        title: 'Token copiado!',
                        text: app.token,
                        timer: 2000,
                        showConfirmButton: false
                      });
                    }}
                    style={{cursor: 'pointer'}}
                    title="Clique para copiar"
                  >
                    {app.token}
                  </code>
                </td>
                <td>
                  <span className={`status-badge ${app.status.toLowerCase()}`}>
                    {app.status}
                  </span>
                </td>
                <td>
                  {app.status === 'Ativo' ? (
                    <span className={`installation-badge ${app.integradora ? 'identified' : 'unknown'}`}
                      title={[app.plataforma_instalacao, app.modelo_dispositivo].filter(Boolean).join(' · ') || 'Ambiente ainda não informado'}>
                      {app.integradora || 'Não informada'}
                    </span>
                  ) : '-'}
                </td>
                <td>
                  {app.device_id ? (
                    <span 
                      className="status-badge ativo" 
                      title={app.device_info || app.device_id}
                      style={{cursor: 'help'}}
                    >
                      Vinculado
                    </span>
                  ) : (
                    <span className="status-badge lancado">
                      Livre
                    </span>
                  )}
                </td>
                <td className="col-mobile-hide">{new Date(app.created_at).toLocaleDateString('pt-BR')}</td>
                <td className="col-mobile-hide">
                  {app.data_ativacao ? new Date(app.data_ativacao).toLocaleDateString('pt-BR') : '-'}
                </td>
                <td>
                  <div className="action-buttons">
                    {!acessoLimitado && <button className="ib ib-blue" title="Editar aplicativo" onClick={() => editarAplicativo(app)}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20h9"/>
                        <path d="M16.5 3.5a2.1 2.1 0 013 3L8 18l-4 1 1-4z"/>
                      </svg>
                      Editar
                    </button>}
                    <button className="ib ib-sky" title="Ver detalhes" onClick={() => verDetalhes(app)}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                      Ver Detalhes
                    </button>
                    {!acessoLimitado && app.status === 'Lancado' && (
                      <>
                        <button className="ib ib-green" title="Ativar aplicativo" onClick={() => ativarAplicativo(app.id)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          Ativar
                        </button>
                        {!app.is_homologacao && <button className="ib ib-red" title="Excluir aplicativo" onClick={() => excluirAplicativo(app.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                          </svg>
                          Excluir
                        </button>}
                      </>
                    )}
                    {!acessoLimitado && app.status === 'Ativo' && (
                      <>
                        <button className="ib ib-amber" title="Inativar aplicativo" onClick={() => inativarAplicativo(app.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
                          </svg>
                          Inativar
                        </button>
                        <button className="ib ib-red" title="Liberar licença para outro dispositivo" onClick={() => desvincularAplicativo(app.id, app.device_info)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 019.9-1"/>
                          </svg>
                          Liberar
                        </button>
                      </>
                    )}
                    {!acessoLimitado && app.status === 'Inativo' && (
                      <>
                        <button className="ib ib-green" title="Ativar aplicativo" onClick={() => ativarAplicativo(app.id)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                          Ativar
                        </button>
                        <button className="ib ib-red" title="Liberar licença para outro dispositivo" onClick={() => desvincularAplicativo(app.id, app.device_info)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 019.9-1"/>
                          </svg>
                          Liberar
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ===================== Detalhes do Aplicativo =====================
const DetalhesAplicativo = ({ app, onBack, startEditing = false }) => {
  const { user, acessoLimitado } = useContext(AppContext);
  const isMaster = user?.role === 'MASTER';
  const [arquivos, setArquivos] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('arquivos');
  const [showEditConfig, setShowEditConfig] = useState(startEditing);
  const [showPedidosModal, setShowPedidosModal] = useState(false);
  const [pedidosVisualizacao, setPedidosVisualizacao] = useState([]);
  const [configData, setConfigData] = useState({
    nome: app.nome,
    modo_aplicacao: app.modo_aplicacao || 1,
    modelo_impressao_pdv: Number(app.modelo_impressao_pdv) === 2 ? 2 : 1,
    imprimir_ficha_por_quantidade: !!app.imprimir_ficha_por_quantidade,
    enviar_carga_cada_pedido: !!app.enviar_carga_cada_pedido,
    url_base: app.url_base || 'http://localhost:9002',
    qtde_mesas: app.qtde_mesas || 50,
    nfce: app.nfce || false,
    nfe: app.nfe || false,
    nao_sair_pesquisa: app.nao_sair_pesquisa || false,
    consumidor_final_padrao: app.consumidor_final_padrao || false,
    habilita_mesas: app.habilita_mesas || false
  });

  const formatarTamanho = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const carregarVisualizacao = async (arquivoId) => {
    try {
      const pedidosDoArquivo = await carregarPedidosDoArquivo(arquivoId);
      if (!pedidosDoArquivo) return;
      setPedidosVisualizacao(pedidosDoArquivo);
      setShowPedidosModal(true);
    } catch (e) {
      Swal.fire('Erro', e.message, 'error');
    }
  };

  useEffect(() => {
    loadDados();
  }, []);

  const loadDados = async () => {
    try {
      const [arquivosRes, pedidosRes] = await Promise.all([
        api.get(`/aplicativos/${app.id}/arquivos-carga`),
        api.get(`/aplicativos/${app.id}/pedidos`)
      ]);
      
      if (arquivosRes.success) setArquivos(arquivosRes.arquivos);
      if (pedidosRes.success) setPedidos(pedidosRes.pedidos);
    } catch (e) {
      console.error('Erro ao carregar dados:', e);
    }
    setLoading(false);
  };

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put(`/aplicativos/${app.id}`, configData);
      if (response.success) {
        Swal.fire('Sucesso', 'Configurações atualizadas com sucesso!', 'success');
        setShowEditConfig(false);
        // Atualizar os dados do app
        Object.assign(app, configData);
      } else {
        Swal.fire('Erro', response.error, 'error');
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao atualizar configurações', 'error');
    }
  };

  if (loading) return <div className="loading">Carregando detalhes...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn-secondary" onClick={onBack}>← Voltar</button>
        <div><span className="eyebrow">APLICATIVO</span><h2>Detalhes: {app.nome}</h2><p>Configurações, cargas e pedidos vinculados a esta licença.</p></div>
      </div>

      {showEditConfig && (
        <div className="modal-overlay">
          <div className="modal app-config-modal">
            <h3>Editar Configurações</h3>
            <form onSubmit={handleUpdateConfig} className="login-form">
              <div className="form-group">
                <label>Nome do Aplicativo</label>
                <input
                  type="text"
                  value={configData.nome}
                  onChange={(e) => setConfigData({ ...configData, nome: e.target.value })}
                  readOnly={!!app.is_homologacao}
                  title={app.is_homologacao ? 'A descrição do APP Homologação é fixa' : ''}
                  required
                />
              </div>
              
              <h4 style={{marginTop: '20px', marginBottom: '10px', fontSize: '16px'}}>Configurações</h4>
              
              <div className="form-group">
                <label>Modo de Aplicação</label>
                <select
                  value={configData.modo_aplicacao}
                  onChange={(e) => setConfigData({ ...configData, modo_aplicacao: parseInt(e.target.value) })}
                  style={{width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd'}}
                >
                  <option value={1}>1 - Força de vendas</option>
                  <option value={2}>2 - PDV</option>
                  {isMaster && <option value={3}>3 - PDV API</option>}
                  {isMaster && <option value={4}>4 - SmartTEF</option>}
                </select>
              </div>

              {!!app.is_homologacao && <div className="demo-edit-note">A descrição “APP Homologação” é fixa. Todas as demais configurações podem ser alteradas.</div>}
              {Number(configData.modo_aplicacao) === 2 && (
                <>
                  <div className="form-group">
                    <label htmlFor="editar-modelo-impressao-pdv">Modelo de impressão do PDV</label>
                    <select
                      id="editar-modelo-impressao-pdv"
                      value={configData.modelo_impressao_pdv}
                      onChange={(e) => setConfigData({ ...configData, modelo_impressao_pdv: Number(e.target.value) })}
                      style={{width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd'}}
                    >
                      <option value={1}>1 - Padrão</option>
                      <option value={2}>2 - Ficha</option>
                    </select>
                  </div>
                  {Number(configData.modelo_impressao_pdv) === 2 && (
                    <label className="app-config-check">
                      <input
                        type="checkbox"
                        checked={configData.imprimir_ficha_por_quantidade}
                        onChange={(e) => setConfigData({ ...configData, imprimir_ficha_por_quantidade: e.target.checked })}
                      />
                      <span>Imprimir uma ficha para cada unidade do item</span>
                    </label>
                  )}
                </>
              )}
              {[1, 2].includes(Number(configData.modo_aplicacao)) && (
                <label className="app-config-check">
                  <input
                    type="checkbox"
                    checked={configData.enviar_carga_cada_pedido}
                    onChange={(e) => setConfigData({ ...configData, enviar_carga_cada_pedido: e.target.checked })}
                  />
                  <span>Envio de carga a cada pedido</span>
                </label>
              )}

              <div className="form-group">
                <label>URL Base</label>
                <input
                  type="text"
                  value={configData.url_base}
                  onChange={(e) => setConfigData({ ...configData, url_base: e.target.value })}
                />
              </div>
              
              <div className="form-group">
                <label>Qtde Mesas</label>
                <input
                  type="number"
                  value={configData.qtde_mesas}
                  onChange={(e) => setConfigData({ ...configData, qtde_mesas: parseInt(e.target.value) })}
                />
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={configData.nfce}
                    onChange={(e) => setConfigData({ ...configData, nfce: e.target.checked })}
                  />
                  <span>NFC-e</span>
                </label>
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={configData.nfe}
                    onChange={(e) => setConfigData({ ...configData, nfe: e.target.checked })}
                  />
                  <span>NF-e</span>
                </label>
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={configData.nao_sair_pesquisa}
                    onChange={(e) => setConfigData({ ...configData, nao_sair_pesquisa: e.target.checked })}
                  />
                  <span>Não sair da tela de pesquisa</span>
                </label>
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={configData.consumidor_final_padrao}
                    onChange={(e) => setConfigData({ ...configData, consumidor_final_padrao: e.target.checked })}
                  />
                  <span>Consumidor final como padrão</span>
                </label>
              </div>
              
              <div className="form-group">
                <label style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                  <input
                    type="checkbox"
                    checked={configData.habilita_mesas}
                    onChange={(e) => setConfigData({ ...configData, habilita_mesas: e.target.checked })}
                  />
                  <span>Habilita controle de mesas</span>
                </label>
              </div>
              
              <div className="modal-buttons">
                <button type="submit" className="btn-primary">Salvar</button>
                <button type="button" className="btn-secondary" onClick={() => setShowEditConfig(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPedidosModal && <PedidosModal pedidos={pedidosVisualizacao} onClose={() => {
        setShowPedidosModal(false);
        setPedidosVisualizacao([]);
      }} />}

      <div className="content-section">
        <div className="info-card">
          <h3>Informações do Aplicativo</h3>
          <div className="app-info">
            <p><strong>Nome:</strong> {app.nome}</p>
            <p>
              <strong>Token:</strong> 
              <code 
                className="token" 
                onClick={() => {
                  navigator.clipboard.writeText(app.token);
                  Swal.fire({
                    icon: 'success',
                    title: 'Token copiado!',
                    text: app.token,
                    timer: 2000,
                    showConfirmButton: false
                  });
                }}
                style={{cursor: 'pointer'}}
                title="Clique para copiar"
              >
                {app.token}
              </code>
            </p>
            <p><strong>Status:</strong> <span className={`status-badge ${app.status.toLowerCase()}`}>{app.status}</span></p>
            <p><strong>Data de Criação:</strong> {formatarData(app.created_at)}</p>
            {app.data_ativacao && (
              <p><strong>Data de Ativação:</strong> {formatarData(app.data_ativacao)}</p>
            )}
          </div>
          
          <h3 style={{marginTop: '20px'}}>Configurações</h3>
          <div style={{
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{display: 'block', marginBottom: '5px', color: '#495057'}}>Modo de Aplicação:</strong>
              <span style={{fontSize: '15px'}}>{
                app.modo_aplicacao === 1 ? '1 - Força de vendas' :
                app.modo_aplicacao === 2 ? '2 - PDV' :
                app.modo_aplicacao === 3 ? '3 - PDV API' :
                app.modo_aplicacao === 4 ? '4 - SmartTEF' : app.modo_aplicacao
              }</span>
            </div>
            
            <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{display: 'block', marginBottom: '5px', color: '#495057'}}>URL Base:</strong>
              <span style={{fontSize: '15px', wordBreak: 'break-all'}}>{app.url_base}</span>
            </div>
            
            <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{display: 'block', marginBottom: '5px', color: '#495057'}}>Qtde Mesas:</strong>
              <span style={{fontSize: '15px'}}>{app.qtde_mesas}</span>
            </div>
            
            <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{display: 'block', marginBottom: '5px', color: '#495057'}}>NFC-e:</strong>
              <span style={{
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                background: app.nfce ? '#28a745' : '#dc3545',
                display: 'inline-block'
              }}>
                {app.nfce ? 'SIM' : 'NÃO'}
              </span>
            </div>
            
            <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{display: 'block', marginBottom: '5px', color: '#495057'}}>NF-e:</strong>
              <span style={{
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                background: app.nfe ? '#28a745' : '#dc3545',
                display: 'inline-block'
              }}>
                {app.nfe ? 'SIM' : 'NÃO'}
              </span>
            </div>
            
            <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{display: 'block', marginBottom: '5px', color: '#495057'}}>Não sair da tela de pesquisa:</strong>
              <span style={{
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                background: app.nao_sair_pesquisa ? '#28a745' : '#dc3545',
                display: 'inline-block'
              }}>
                {app.nao_sair_pesquisa ? 'SIM' : 'NÃO'}
              </span>
            </div>
            
            <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{display: 'block', marginBottom: '5px', color: '#495057'}}>Consumidor final como padrão:</strong>
              <span style={{
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                background: app.consumidor_final_padrao ? '#28a745' : '#dc3545',
                display: 'inline-block'
              }}>
                {app.consumidor_final_padrao ? 'SIM' : 'NÃO'}
              </span>
            </div>
            
            <div style={{padding: '10px', background: '#f8f9fa', borderRadius: '6px'}}>
              <strong style={{display: 'block', marginBottom: '5px', color: '#495057'}}>Habilita controle de mesas:</strong>
              <span style={{
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: 'bold',
                color: 'white',
                background: app.habilita_mesas ? '#28a745' : '#dc3545',
                display: 'inline-block'
              }}>
                {app.habilita_mesas ? 'SIM' : 'NÃO'}
              </span>
            </div>
          </div>
          {!acessoLimitado && <button
            className="btn-primary"
            onClick={() => setShowEditConfig(true)}
            style={{marginTop: '15px'}}
          >
            Editar Configurações
          </button>}
        </div>

        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'arquivos' ? 'active' : ''}`}
            onClick={() => setActiveTab('arquivos')}
          >
            Arquivos de Carga ({arquivos.length})
          </button>
          <button 
            className={`tab ${activeTab === 'pedidos' ? 'active' : ''}`}
            onClick={() => setActiveTab('pedidos')}
          >
            Pedidos ({pedidos.length})
          </button>
        </div>

        {activeTab === 'arquivos' && (
          <div className="tab-content">
            <h3>Arquivos de Carga</h3>
            {arquivos.length === 0 ? (
              <p className="no-data">Nenhum arquivo de carga encontrado para este aplicativo</p>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nome do Arquivo</th>
                      <th>Tamanho</th>
                      <th>Data Recebimento</th>
                      <th>Última Atualização</th>
                      <th>Total Solicitações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {arquivos.map(arquivo => (
                      <tr key={arquivo.id}>
                        <td>{arquivo.nome_arquivo}</td>
                        <td>
                          <span className="badge badge-tamanho">
                            {formatarTamanho(arquivo.tamanho_bytes)}
                          </span>
                        </td>
                        <td>{formatarData(arquivo.data_recebimento)}</td>
                        <td>{formatarData(arquivo.data_atualizacao)}</td>
                        <td>
                          <span className="badge">{arquivo.total_solicitacoes}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'pedidos' && (
          <div className="tab-content">
            <h3>Pedidos Recebidos</h3>
            {pedidos.length === 0 ? (
              <p className="no-data">Nenhum pedido encontrado para este aplicativo</p>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nome do Arquivo</th>
                      <th>Tamanho</th>
                      <th>Data Recebimento</th>
                      <th>Status</th>
                      <th>Data Sincronização</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pedidos.map(pedido => (
                      <tr key={pedido.id}>
                        <td>{pedido.nome_arquivo}</td>
                        <td>
                          <span className="badge badge-tamanho">
                            {formatarTamanho(pedido.tamanho_bytes)}
                          </span>
                        </td>
                        <td>{formatarData(pedido.data_recebimento)}</td>
                        <td>
                          <span className={`status-badge ${pedido.status.toLowerCase()}`}>
                            {pedido.status}
                          </span>
                        </td>
                        <td>{formatarData(pedido.data_sincronizacao)}</td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-action btn-info"
                              onClick={() => carregarVisualizacao(pedido.id)}
                              title="Visualizar pedidos"
                            >
                              👁️ Ver
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ===================== Dados de Carga =====================
const DadosCarga = () => {
  const { user, acessoLimitado } = useContext(AppContext);
  const [arquivos, setArquivos] = useState([]);
  const [aplicativos, setAplicativos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(localStorage.getItem('clienteSelecionadoId') || '');
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [selectedApp, setSelectedApp] = useState('');

  const isSoftwareHouse = user?.tipo === 'software_house';

  const formatarTamanho = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    const iniciar = async () => {
      if (isSoftwareHouse) {
        const lista = await api.get('/clientes');
        const clientesValidos = Array.isArray(lista) ? lista : [];
        setClientes(clientesValidos);
        const atual = localStorage.getItem('clienteSelecionadoId');
        if (!atual && clientesValidos.length) {
          const primeiro = String(clientesValidos[0].id);
          localStorage.setItem('clienteSelecionadoId', primeiro);
          localStorage.setItem('clienteSelecionadoNome', clientesValidos[0].nome_fantasia || clientesValidos[0].razao_social);
          setClienteSelecionado(primeiro);
        }
      }
      await Promise.all([loadAplicativos(), loadArquivos()]);
    };
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selecionarCliente = async (id) => {
    setClienteSelecionado(id);
    if (id) {
      localStorage.setItem('clienteSelecionadoId', id);
      const c = clientes.find(cl => String(cl.id) === String(id));
      if (c) localStorage.setItem('clienteSelecionadoNome', c.nome_fantasia || c.razao_social);
    } else {
      localStorage.removeItem('clienteSelecionadoId');
      localStorage.removeItem('clienteSelecionadoNome');
    }
    setLoading(true);
    await Promise.all([loadAplicativos(), loadArquivos()]);
  };

  const loadAplicativos = async () => {
    try {
      const response = await api.get('/aplicativos');
      setAplicativos(Array.isArray(response) ? response : []);
    } catch (e) {
      console.error('Erro ao carregar aplicativos:', e);
    }
  };

  const loadArquivos = async () => {
    try {
      const response = await api.get('/admin/arquivos-carga');
      setArquivos(Array.isArray(response) ? response : []);
    } catch (e) {
      console.error('Erro ao carregar arquivos:', e);
    }
    setLoading(false);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      Swal.fire('Erro', 'Selecione um arquivo JSON', 'error');
      return;
    }
    if (!selectedApp) {
      Swal.fire('Erro', 'Selecione um aplicativo', 'error');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          const response = await api.post('/arquivos-carga', {
            aplicativo_id: selectedApp,
            nome_arquivo: uploadFile.name,
            dados_json: JSON.stringify(jsonData)
          });
          
          if (response.success) {
            Swal.fire('Sucesso', 'Arquivo de carga enviado com sucesso!', 'success');
            setShowUpload(false);
            setUploadFile(null);
            setSelectedApp('');
            loadArquivos();
          } else {
            Swal.fire('Erro', response.error, 'error');
          }
        } catch (parseError) {
          Swal.fire('Erro', 'Arquivo JSON inválido', 'error');
        }
      };
      reader.readAsText(uploadFile);
    } catch (e) {
      Swal.fire('Erro', 'Erro ao processar arquivo', 'error');
    }
  };

  const excluirArquivo = async (id) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Deseja excluir este arquivo de carga?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.delete(`/arquivos-carga/${id}`);
        if (response.success) {
          Swal.fire('Sucesso', 'Arquivo excluído!', 'success');
          loadArquivos();
        } else {
          Swal.fire('Erro', response.error, 'error');
        }
      } catch (e) {
        Swal.fire('Erro', 'Erro ao excluir arquivo', 'error');
      }
    }
  };

  const downloadArquivoJSON = async (arquivo) => {
    try {
      // Buscar dados completos da API
      const response = await api.get(`/arquivos-carga/${arquivo.id}/download`);
      
      if (!response.success) {
        Swal.fire('Erro', response.error || 'Erro ao baixar arquivo', 'error');
        return;
      }
      
      let jsonData = response.dados_json;
      
      // Parse se for string
      if (typeof jsonData === 'string') {
        jsonData = JSON.parse(jsonData);
      }
      
      // Converter para string formatada
      const jsonString = JSON.stringify(jsonData, null, 2);
      
      // Criar blob e link para download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.nome_arquivo || arquivo.nome_arquivo || `arquivo_carga_${arquivo.id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erro ao baixar arquivo:', e);
      Swal.fire('Erro', 'Erro ao processar o arquivo JSON para download', 'error');
    }
  };

  if (loading) return <div className="loading">Carregando dados de carga...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div><span className="eyebrow">SINCRONIZAÇÃO</span><h2>Carga de Dados</h2><p>Envie, consulte e acompanhe os arquivos publicados para cada aplicativo.</p></div>
        {!acessoLimitado && <button className="btn-primary" onClick={() => setShowUpload(true)}>Upload de arquivo</button>}
      </div>

      {isSoftwareHouse && clientes.length > 0 && (
        <div className="client-selector-bar">
          <label>Cliente:</label>
          <select value={clienteSelecionado} onChange={e => selecionarCliente(e.target.value)}>
            <option value="">— Selecione um cliente —</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
          {!clienteSelecionado && <span className="hint">Selecione um cliente para ver os arquivos de carga</span>}
        </div>
      )}
      
      <div className="content-section">
        <div className="info-card">
          <h3>Informações</h3>
          <p>Esta tela lista todos os arquivos JSON de carga vinculados aos aplicativos.</p>
        </div>

        {showUpload && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Upload de Arquivo de Carga</h3>
              <form onSubmit={handleFileUpload} className="login-form">
                <div className="form-group">
                  <label>Selecione o aplicativo:</label>
                  <select
                    value={selectedApp}
                    onChange={(e) => setSelectedApp(e.target.value)}
                    required
                  >
                    <option value="">-- Selecione um aplicativo --</option>
                    {aplicativos.map(app => (
                      <option key={app.id} value={app.id}>
                        {app.nome} ({app.status})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Selecione o arquivo JSON:</label>
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    required
                  />
                </div>
                <div className="modal-buttons">
                  <button type="submit" className="btn-primary">Enviar Arquivo</button>
                  <button type="button" className="btn-secondary" onClick={() => {
                    setShowUpload(false);
                    setUploadFile(null);
                    setSelectedApp('');
                  }}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Aplicativo</th>
                <th className="col-mobile-hide">Nome do Arquivo</th>
                <th className="col-mobile-hide">Tamanho</th>
                <th className="col-mobile-hide">Data Recebimento</th>
                <th className="col-mobile-hide">Última Atualização</th>
                <th className="col-mobile-hide">Total Solicitações</th>
                <th className="col-mobile-hide">Logs de Acesso</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {arquivos.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">Nenhum arquivo de carga encontrado</td>
                </tr>
              ) : (
                arquivos.map(arquivo => (
                  <tr key={arquivo.id}>
                    <td><strong>{arquivo.aplicativo_nome}</strong></td>
                    <td className="col-mobile-hide">{arquivo.nome_arquivo}</td>
                    <td className="col-mobile-hide">
                      <span className="badge badge-tamanho">
                        {formatarTamanho(arquivo.tamanho_bytes)}
                      </span>
                    </td>
                    <td className="col-mobile-hide">{formatarData(arquivo.data_recebimento)}</td>
                    <td className="col-mobile-hide">{formatarData(arquivo.data_atualizacao)}</td>
                    <td className="col-mobile-hide">
                      <span className="badge badge-solicitacoes">{arquivo.total_solicitacoes}</span>
                    </td>
                    <td className="col-mobile-hide">
                      <span className="badge badge-logs">{arquivo.total_logs || 0}</span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="ib ib-sky" title="Baixar arquivo JSON" onClick={() => downloadArquivoJSON(arquivo)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Download
                        </button>
                        {!acessoLimitado && <button className="ib ib-red" title="Excluir arquivo" onClick={() => excluirArquivo(arquivo.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                          </svg>
                          Excluir
                        </button>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ===================== Pedidos =====================
const Pedidos = () => {
  const { user, acessoLimitado } = useContext(AppContext);
  const [pedidos, setPedidos] = useState([]);
  const [aplicativos, setAplicativos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(localStorage.getItem('clienteSelecionadoId') || '');
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [selectedApp, setSelectedApp] = useState('');
  const [filtroApp, setFiltroApp] = useState('');
  const [showPedidosModal, setShowPedidosModal] = useState(false);
  const [pedidosVisualizacao, setPedidosVisualizacao] = useState([]);

  const isSoftwareHouse = user?.tipo === 'software_house';

  const formatarTamanho = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  useEffect(() => {
    const iniciar = async () => {
      if (isSoftwareHouse) {
        const lista = await api.get('/clientes');
        const clientesValidos = Array.isArray(lista) ? lista : [];
        setClientes(clientesValidos);
        const atual = localStorage.getItem('clienteSelecionadoId');
        if (!atual && clientesValidos.length) {
          const primeiro = String(clientesValidos[0].id);
          localStorage.setItem('clienteSelecionadoId', primeiro);
          localStorage.setItem('clienteSelecionadoNome', clientesValidos[0].nome_fantasia || clientesValidos[0].razao_social);
          setClienteSelecionado(primeiro);
        }
      }
      await Promise.all([loadAplicativos(), loadPedidos()]);
    };
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selecionarCliente = async (id) => {
    setClienteSelecionado(id);
    setFiltroApp('');
    if (id) {
      localStorage.setItem('clienteSelecionadoId', id);
      const c = clientes.find(cl => String(cl.id) === String(id));
      if (c) localStorage.setItem('clienteSelecionadoNome', c.nome_fantasia || c.razao_social);
    } else {
      localStorage.removeItem('clienteSelecionadoId');
      localStorage.removeItem('clienteSelecionadoNome');
    }
    setLoading(true);
    await Promise.all([loadAplicativos(), loadPedidos()]);
  };

  const loadAplicativos = async () => {
    try {
      const response = await api.get('/aplicativos');
      setAplicativos(Array.isArray(response) ? response : []);
    } catch (e) {
      console.error('Erro ao carregar aplicativos:', e);
    }
  };

  const loadPedidos = async () => {
    try {
      let url = '/admin/pedidos';
      if (filtroApp) {
        url += `?aplicativo_id=${filtroApp}`;
      }
      const response = await api.get(url);
      setPedidos(Array.isArray(response) ? response : []);
    } catch (e) {
      console.error('Erro ao carregar pedidos:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      loadPedidos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroApp]);

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      Swal.fire('Erro', 'Selecione um arquivo JSON', 'error');
      return;
    }
    if (!selectedApp) {
      Swal.fire('Erro', 'Selecione um aplicativo', 'error');
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          const response = await api.post('/pedidos-recebidos', {
            aplicativo_id: selectedApp,
            nome_arquivo: uploadFile.name,
            dados_json: JSON.stringify(jsonData)
          });
          
          if (response.success) {
            Swal.fire('Sucesso', 'Pedido enviado com sucesso!', 'success');
            setShowUpload(false);
            setUploadFile(null);
            setSelectedApp('');
            loadPedidos();
          } else {
            Swal.fire('Erro', response.error, 'error');
          }
        } catch (parseError) {
          Swal.fire('Erro', 'Arquivo JSON inválido', 'error');
        }
      };
      reader.readAsText(uploadFile);
    } catch (e) {
      Swal.fire('Erro', 'Erro ao processar arquivo', 'error');
    }
  };

  const sincronizarPedido = async (id) => {
    try {
      const response = await api.post(`/pedidos/${id}/sincronizar`);
      if (response.success) {
        Swal.fire('Sucesso', 'Pedido sincronizado!', 'success');
        loadPedidos();
      } else {
        Swal.fire('Erro', response.error, 'error');
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao sincronizar pedido', 'error');
    }
  };

  const excluirPedido = async (id) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Deseja excluir este pedido?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const response = await api.delete(`/pedidos/${id}`);
        if (response.success) {
          Swal.fire('Sucesso', 'Pedido excluído!', 'success');
          loadPedidos();
        } else {
          Swal.fire('Erro', response.error, 'error');
        }
      } catch (e) {
        Swal.fire('Erro', 'Erro ao excluir pedido', 'error');
      }
    }
  };

  const downloadPedidoJSON = async (pedido) => {
    try {
      // Buscar dados completos da API
      const response = await api.get(`/pedidos/${pedido.id}/download`);
      
      if (!response.success) {
        Swal.fire('Erro', response.error || 'Erro ao baixar pedido', 'error');
        return;
      }
      
      let jsonData = response.dados_json;
      
      // Parse se for string
      if (typeof jsonData === 'string') {
        jsonData = JSON.parse(jsonData);
      }
      
      // Converter para string formatada
      const jsonString = JSON.stringify(jsonData, null, 2);
      
      // Criar blob e link para download
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.nome_arquivo || pedido.nome_arquivo || `pedido_${pedido.id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Erro ao baixar pedido:', e);
      Swal.fire('Erro', 'Erro ao processar o arquivo JSON para download', 'error');
    }
  };

  const carregarVisualizacao = async (arquivoId) => {
    try {
      const pedidosDoArquivo = await carregarPedidosDoArquivo(arquivoId);
      if (!pedidosDoArquivo) return;
      setPedidosVisualizacao(pedidosDoArquivo);
      setShowPedidosModal(true);
    } catch (e) {
      Swal.fire('Erro', e.message, 'error');
    }
  };

  if (loading) return <div className="loading">Carregando pedidos...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <div><span className="eyebrow">OPERAÇÃO</span><h2>Carga de Pedidos</h2><p>Acompanhe os pedidos recebidos e o andamento da sincronização com o ERP.</p></div>
        {!acessoLimitado && <button className="btn-primary" onClick={() => setShowUpload(true)}>Upload de pedido</button>}
      </div>

      {isSoftwareHouse && clientes.length > 0 && (
        <div className="client-selector-bar">
          <label>Cliente:</label>
          <select value={clienteSelecionado} onChange={e => selecionarCliente(e.target.value)}>
            <option value="">— Selecione um cliente —</option>
            {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</option>)}
          </select>
          {!clienteSelecionado && <span className="hint">Selecione um cliente para ver os pedidos</span>}
        </div>
      )}
      
      <div className="content-section">
        <div className="info-card">
          <h3>Informações</h3>
          <p>Esta tela lista todos os pedidos recebidos dos aplicativos.</p>
        </div>

        <div className="filter-section">
          <label>Filtrar por aplicativo:</label>
          <select value={filtroApp} onChange={(e) => setFiltroApp(e.target.value)}>
            <option value="">Todos os aplicativos</option>
            {aplicativos.map(app => (
              <option key={app.id} value={app.id}>{app.nome}</option>
            ))}
          </select>
        </div>

        {showUpload && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>Upload de Pedido</h3>
              <form onSubmit={handleFileUpload} className="login-form">
                <div className="form-group">
                  <label>Selecione o aplicativo:</label>
                  <select
                    value={selectedApp}
                    onChange={(e) => setSelectedApp(e.target.value)}
                    required
                  >
                    <option value="">-- Selecione um aplicativo --</option>
                    {aplicativos.map(app => (
                      <option key={app.id} value={app.id}>
                        {app.nome} ({app.status})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Selecione o arquivo JSON do pedido:</label>
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    required
                  />
                </div>
                <div className="modal-buttons">
                  <button type="submit" className="btn-primary">Enviar Pedido</button>
                  <button type="button" className="btn-secondary" onClick={() => {
                    setShowUpload(false);
                    setUploadFile(null);
                    setSelectedApp('');
                  }}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showPedidosModal && <PedidosModal pedidos={pedidosVisualizacao} onClose={() => {
          setShowPedidosModal(false);
          setPedidosVisualizacao([]);
        }} />}

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Aplicativo</th>
                <th className="col-mobile-hide">Nome do Arquivo</th>
                <th className="col-mobile-hide">Tamanho</th>
                <th className="col-mobile-hide">Data Recebimento</th>
                <th>Status</th>
                <th className="col-mobile-hide">Data Sincronização</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">Nenhum pedido encontrado</td>
                </tr>
              ) : (
                pedidos.map(pedido => (
                  <tr key={pedido.id}>
                    <td><strong>{pedido.aplicativo_nome}</strong></td>
                    <td className="col-mobile-hide">{pedido.nome_arquivo}</td>
                    <td className="col-mobile-hide">
                      <span className="badge badge-tamanho">
                        {formatarTamanho(pedido.tamanho_bytes)}
                      </span>
                    </td>
                    <td className="col-mobile-hide">{formatarData(pedido.data_recebimento)}</td>
                    <td>
                      <span className={`status-badge ${pedido.status.toLowerCase()}`}>
                        {pedido.status}
                      </span>
                    </td>
                    <td className="col-mobile-hide">{formatarData(pedido.data_sincronizacao)}</td>
                    <td>
                      <div className="action-buttons">
                        <button className="ib ib-blue" title="Visualizar pedidos do JSON" onClick={() => carregarVisualizacao(pedido.id)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                          Ver
                        </button>
                        <button className="ib ib-sky" title="Baixar arquivo JSON" onClick={() => downloadPedidoJSON(pedido)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Download
                        </button>
                        {!acessoLimitado && <button className="ib ib-red" title="Excluir pedido" onClick={() => excluirPedido(pedido.id)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                          </svg>
                          Excluir
                        </button>}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ===================== Profile =====================
const Profile = () => {
  const { user, updateProfile, acessoLimitado } = useContext(AppContext);
  const [profile, setProfile] = useState({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '', newPassword: '', confirmPassword: ''
  });
  const [loading, setLoading] = useState(true);
  const { municipios, carregandoMunicipios } = useMunicipios(profile.uf);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await api.get('/profile');
      setProfile(data);
    } catch (e) {
      console.error('Erro ao carregar perfil:', e);
    }
    setLoading(false);
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.put('/profile', profile);
      if (response.success) {
        Swal.fire('Sucesso', 'Perfil atualizado com sucesso!', 'success');
        updateProfile(profile);
      } else {
        Swal.fire('Erro', response.error, 'error');
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao atualizar perfil', 'error');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      Swal.fire('Erro', 'As senhas não coincidem', 'error');
      return;
    }

    try {
      const response = await api.put('/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      if (response.success) {
        Swal.fire('Sucesso', 'Senha alterada com sucesso!', 'success');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        Swal.fire('Erro', response.error, 'error');
      }
    } catch (e) {
      Swal.fire('Erro', 'Erro ao alterar senha', 'error');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfile({ ...profile, logo_base64: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) return <div className="loading">Carregando perfil...</div>;

  return (
    <div className="page-container">
      <div className="page-header"><div><span className="eyebrow">CONFIGURAÇÕES</span><h2>Perfil da Software House</h2><p>Dados cadastrais, identidade visual e segurança da conta.</p></div></div>
      
      <div className="profile-container">
        <div className="profile-section">
          <h3 className="profile-section-title">Dados da Empresa</h3>
          <form onSubmit={handleProfileSubmit}>
            <div className="profile-form-grid">
              <div className="profile-field">
                <label>Razão Social *</label>
                <input type="text" placeholder="Razão Social" value={profile.razao_social || ''} onChange={(e) => setProfile({ ...profile, razao_social: e.target.value })} required />
              </div>
              <div className="profile-field">
                <label>Nome Fantasia</label>
                <input type="text" placeholder="Nome Fantasia" value={profile.nome_fantasia || ''} onChange={(e) => setProfile({ ...profile, nome_fantasia: e.target.value })} />
              </div>
              <div className="profile-field">
                <label>CPF/CNPJ</label>
                <input type="text" inputMode="numeric" maxLength="18" placeholder="CPF ou CNPJ" value={profile.cnpj || ''} onChange={(e) => setProfile({ ...profile, cnpj: formatCpfCnpj(e.target.value) })} />
              </div>
              <div className="profile-field">
                <label>Inscrição Estadual</label>
                <input type="text" placeholder="IE" value={profile.ie || ''} onChange={(e) => setProfile({ ...profile, ie: e.target.value })} />
              </div>
              <div className="profile-field pf-full">
                <label>Endereço</label>
                <input type="text" placeholder="Rua / Av." value={profile.endereco || ''} onChange={(e) => setProfile({ ...profile, endereco: e.target.value })} />
              </div>
              <div className="profile-field">
                <label>Número</label>
                <input type="text" placeholder="Nº" value={profile.numero || ''} onChange={(e) => setProfile({ ...profile, numero: e.target.value })} />
              </div>
              <div className="profile-field">
                <label>Complemento</label>
                <input type="text" placeholder="Apto, sala…" value={profile.complemento || ''} onChange={(e) => setProfile({ ...profile, complemento: e.target.value })} />
              </div>
              <div className="profile-field">
                <label>Bairro</label>
                <input type="text" placeholder="Bairro" value={profile.bairro || ''} onChange={(e) => setProfile({ ...profile, bairro: e.target.value })} />
              </div>
              <div className="profile-field">
                <label>CEP</label>
                <input type="text" placeholder="00000-000" value={profile.cep || ''} onChange={(e) => setProfile({ ...profile, cep: e.target.value })} />
              </div>
              <div className="profile-field">
                <label>UF</label>
                <select value={profile.uf || ''} onChange={(e) => setProfile({ ...profile, uf: e.target.value, cidade: '' })}>
                  <option value="">Selecione a UF</option>
                  {UFS_BRASIL.map(([sigla, nome]) => <option key={sigla} value={sigla}>{sigla} — {nome}</option>)}
                </select>
              </div>
              <div className="profile-field">
                <label>Cidade</label>
                <select value={profile.cidade || ''} onChange={(e) => setProfile({ ...profile, cidade: e.target.value })} disabled={!profile.uf || carregandoMunicipios}>
                  <option value="">{carregandoMunicipios ? 'Carregando cidades...' : profile.uf ? 'Selecione a cidade' : 'Selecione primeiro a UF'}</option>
                  {profile.cidade && !municipios.includes(profile.cidade) && <option value={profile.cidade}>{profile.cidade}</option>}
                  {municipios.map((municipio) => <option key={municipio} value={municipio}>{municipio}</option>)}
                </select>
              </div>
              <div className="profile-field">
                <label>WhatsApp</label>
                <input type="text" placeholder="(11) 90000-0000" value={profile.whatsapp || ''} onChange={(e) => setProfile({ ...profile, whatsapp: e.target.value })} />
              </div>
            </div>
            {!acessoLimitado && <button type="submit" className="btn-primary profile-save-btn">Salvar Dados</button>}
          </form>
        </div>

        <div className="profile-section">
          <h3 className="profile-section-title">Personalização</h3>
          {/* Seletores de cores temporariamente desabilitados.
          <div className="profile-color-row">
            <div className="profile-color-field">
              <label>Cor Primária</label>
              <input type="color" value={profile.tema_cor_primaria || '#007bff'} onChange={(e) => setProfile({ ...profile, tema_cor_primaria: e.target.value })} />
            </div>
            <div className="profile-color-field">
              <label>Cor Secundária</label>
              <input type="color" value={profile.tema_cor_secundaria || '#6c757d'} onChange={(e) => setProfile({ ...profile, tema_cor_secundaria: e.target.value })} />
            </div>
          </div>
          */}
          <form className="profile-logo-editor" onSubmit={handleProfileSubmit}>
            <div className="profile-logo-preview-panel">
              {profile.logo_base64
                ? <img src={profile.logo_base64} alt="Prévia do logotipo da empresa" />
                : <div className="profile-logo-empty"><strong>Seu logotipo aqui</strong><span>Sem imagem personalizada.<br />O app utiliza o logotipo padrão.</span></div>}
            </div>
            <div className="profile-logo-controls">
              <label htmlFor="profile-logo-upload">Logotipo da empresa</label>
              <p>Sua marca nas telas de abertura e login do DT Venda.</p>
              <input id="profile-logo-upload" type="file" accept="image/*" onChange={handleLogoChange} />
              <small>Escolha uma imagem nítida. PNGs transparentes são exibidos sobre fundo branco.</small>
              <div className="profile-logo-actions">
                {!acessoLimitado && <button type="submit" className="btn-primary">Salvar logotipo</button>}
                {profile.logo_base64 && !acessoLimitado && <button type="button" className="btn-secondary" onClick={() => setProfile({ ...profile, logo_base64: null })}>Remover logotipo</button>}
              </div>
              <small>Salve para aplicar a alteração. Os aplicativos atualizam a imagem na próxima consulta online e mantêm uma cópia para uso offline.</small>
            </div>
          </form>
        </div>

        <div className="profile-section">
          <h3 className="profile-section-title">Alterar Senha</h3>
          <form onSubmit={handlePasswordSubmit}>
            <div className="pw-form-grid">
              <div className="profile-field">
                <label>Senha Atual</label>
                <input type="password" placeholder="••••••••" value={passwordData.currentPassword} onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })} required />
              </div>
              <div className="profile-field">
                <label>Nova Senha</label>
                <input type="password" placeholder="••••••••" value={passwordData.newPassword} onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })} required />
              </div>
              <div className="profile-field">
                <label>Confirmar Nova Senha</label>
                <input type="password" placeholder="••••••••" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} required />
              </div>
            </div>
            <button type="submit" className="btn-primary profile-save-btn">Alterar Senha</button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ===================== Faturas =====================
// Aba Faturas: sempre somente as faturas do titular logado (SH ou master).
const Faturas = () => {
  const { atualizarAcesso } = useContext(AppContext);
  const [faturas, setFaturas] = useState([]);
  const [faturaExpandida, setFaturaExpandida] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);

  useEffect(() => {
    sincronizarECarregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sincronizarECarregar = async () => {
    setLoading(true);
    setSincronizando(true);
    try {
      await api.post('/faturas/sincronizar');
      await atualizarAcesso();
    } catch (e) {
      console.error('Erro na sincronização:', e);
    }
    setSincronizando(false);
    await loadFaturas();
  };

  const loadFaturas = async () => {
    try {
      const response = await api.get('/faturas');
      setFaturas(Array.isArray(response) ? response : (response.data || []));
    } catch (e) {
      console.error('Erro ao carregar faturas:', e);
      setFaturas([]);
    }
    setLoading(false);
  };

  const sincronizarStatus = async () => {
    try {
      setSincronizando(true);
      const response = await api.post('/faturas/sincronizar');
      await atualizarAcesso();
      if (response.success) Swal.fire('Sucesso', 'Status das faturas atualizado', 'success');
      await loadFaturas();
    } catch (e) {
      Swal.fire('Erro', 'Erro ao sincronizar faturas', 'error');
      console.error('Erro na sincronização:', e);
    } finally {
      setSincronizando(false);
    }
  };

  if (loading) return <div className="loading">{sincronizando ? '🔄 Sincronizando com Asaas...' : 'Carregando faturas...'}</div>;

  return (
    <div className="content-section">
      <div className="page-header">
        <div><span className="eyebrow">FINANCEIRO</span><h2>Faturas e pagamentos</h2><p>Acompanhe cobranças, vencimentos e comprovantes.</p></div>
        <button onClick={sincronizarStatus} className="btn-primary" disabled={sincronizando}>
          {sincronizando ? 'Sincronizando...' : 'Atualizar Status'}
        </button>
      </div>

      {faturas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>Nenhuma fatura encontrada</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Detalhes</th>
                <th>Competência</th>
                <th>Descrição</th>
                <th>Valor</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Tipo</th>
                <th>Apps</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {faturas.map(fatura => (
                <React.Fragment key={fatura.id}>
                <tr>
                  <td>
                    <button className="sh-expand-button" aria-expanded={faturaExpandida === fatura.id} aria-controls={`fatura-detalhe-${fatura.id}`}
                      aria-label={`${faturaExpandida === fatura.id ? 'Recolher' : 'Ver'} aplicativos considerados na fatura`}
                      title={faturaExpandida === fatura.id ? 'Recolher composição' : 'Ver como o valor foi calculado'}
                      onClick={() => setFaturaExpandida(atual => atual === fatura.id ? null : fatura.id)}>
                      {faturaExpandida === fatura.id ? '−' : '+'}
                    </button>
                  </td>
                  <td>{formatarCompetencia(fatura.mes_referencia)}</td>
                  <td className="fatura-descricao">{fatura.descricao}</td>
                  <td className="fatura-valor">{moedaBRL(fatura.valor)}</td>
                  <td>{fatura.vencimento_formatado}</td>
                  <td>
                    <span className={`status-badge ${classeStatusFatura(fatura.status)}`}>
                      {textoStatusFatura(fatura.status)}
                    </span>
                  </td>
                  <td>{fatura.tipo_cobranca}</td>
                  <td>{fatura.quantidade_aplicativos}</td>
                  <td><AcoesFatura fatura={fatura} api={api} /></td>
                </tr>
                {faturaExpandida === fatura.id && <tr className="fatura-detalhe-row" id={`fatura-detalhe-${fatura.id}`}>
                  <td colSpan="9"><FaturaDetalhes fatura={fatura} api={api} /></td>
                </tr>}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


// ===================== Main App =====================
const App = () => {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('aplicativos');
  const [profile, setProfile] = useState({});
  const [acessoLimitado, setAcessoLimitado] = useState(false);

  const atualizarAcesso = async () => {
    const perfil = await api.get('/profile');
    setAcessoLimitado(!!perfil?.acesso_limitado);
    return !!perfil?.acesso_limitado;
  };

  // A conta bloqueada abre direto em Faturas, onde pode regularizar o pagamento.
  useEffect(() => {
    if (!user) return;
    atualizarAcesso()
      .then(limitado => { if (limitado) setCurrentPage('faturas'); })
      .catch(e => console.error('Erro ao verificar o acesso:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      const restored = JSON.parse(userData);
      setUser(restored);
      setCurrentPage(restored.role === 'MASTER' ? 'dashboard' : restored.tipo === 'software_house' ? 'clientes' : 'aplicativos');
    }
  }, []);

  // Mantem todas as informacoes das tabelas legiveis no mobile. Os rotulos
  // sao derivados dos cabecalhos, inclusive quando os dados chegam via API.
  useEffect(() => {
    if (!user) return undefined;

    const main = document.querySelector('.admin-shell .admin-main');
    if (!main) return undefined;

    const applyTableLabels = () => {
      main.querySelectorAll('table').forEach((table) => {
        const labels = Array.from(table.querySelectorAll('thead th'))
          .map((header) => header.textContent.trim());

        table.querySelectorAll('tbody tr').forEach((row) => {
          Array.from(row.children).forEach((cell, index) => {
            const label = labels[index] || 'Informacao';
            if (cell.tagName === 'TD' && !cell.hasAttribute('colspan') && cell.dataset.label !== label) {
              cell.dataset.label = label;
            }
          });
        });
      });
    };

    // Agrupa as mutacoes (ex.: digitacao em filtros) em uma passada por frame.
    let frame = 0;
    const scheduleLabels = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; applyTableLabels(); });
    };

    applyTableLabels();
    const observer = new MutationObserver(scheduleLabels);
    observer.observe(main, { childList: true, subtree: true });

    const nav = document.querySelector('.admin-shell .nav-tabs');
    const active = nav?.querySelector('.nav-tab.active');
    if (nav && active && window.innerWidth <= 800) {
      nav.scrollTo({
        left: Math.max(0, active.offsetLeft - ((nav.clientWidth - active.offsetWidth) / 2)),
        behavior: 'smooth'
      });
    }

    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [currentPage, user]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setCurrentPage(userData.role === 'MASTER' ? 'dashboard' : userData.tipo === 'software_house' ? 'clientes' : 'aplicativos');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('clienteSelecionadoId');
    localStorage.removeItem('clienteSelecionadoNome');
    setUser(null);
    setAcessoLimitado(false);
  };

  const updateProfile = (newProfile) => {
    setProfile(newProfile);
  };

  const isMaster = user?.role === 'MASTER' || user?.email === 'tiagopassarella@hotmail.com';
  const isSoftwareHouse = user?.tipo === 'software_house';

  if (!user) {
    return (
      <AppContext.Provider value={{ login }}>
        <Login />
      </AppContext.Provider>
    );
  }

  const renderPage = () => {
    if (isMaster && currentPage === 'dashboard') return <MasterDashboard />;
    if (isMaster && currentPage === 'planos') return <PlanosAdmin />;
    switch (currentPage) {
      case 'clientes': return <Clientes onOpenApps={() => setCurrentPage('aplicativos')} />;
      case 'aplicativos': return <Aplicativos />;
      case 'dados-carga': return <DadosCarga />;
      case 'pedidos': return <Pedidos />;
      case 'pagamentos': return <Pagamentos api={api} isSoftwareHouse={isSoftwareHouse} />;
      case 'faturas': return <Faturas />;
      case 'integracao': return <Integracao />;
      case 'profile': return <Profile />;
      default: return <Aplicativos />;
    }
  };

  return (
    <AppContext.Provider value={{ user, logout, updateProfile, profile, acessoLimitado, atualizarAcesso }}>
      <div className={`app-container admin-shell page-${currentPage}`}>
        <header className="header">
          <div className="header-content">
            <div className="portal-brand">
              <span className="portal-logo">DT</span><div>
              <h1 className="header-title">DT Venda</h1>
              <p className="header-subtitle">{user.razao_social}</p>
            </div></div>
            <div className="user-info">
              <span className="user-avatar">{(user.razao_social || 'DT').charAt(0)}</span>
              <span className="user-welcome"><small>Bem-vindo</small>{user.razao_social}</span>
              <button onClick={logout} className="logout-btn">Sair</button>
            </div>
          </div>
        </header>

        <div className="nav-container">
          <div className="mobile-nav-select">
            <span>Menu</span>
            <select value={currentPage} onChange={(e) => setCurrentPage(e.target.value)} aria-label="Navegar pelo portal">
              {isMaster && <option value="dashboard">Visão geral</option>}
              {isMaster && <option value="planos">Planos</option>}
              {isSoftwareHouse && <option value="clientes">Clientes</option>}
              <option value="aplicativos">Aplicativos</option>
              <option value="dados-carga">Carga de Dados</option>
              <option value="pedidos">Carga de Pedidos</option>
              <option value="pagamentos">Pedidos</option>
              <option value="faturas">Faturas</option>
              {isSoftwareHouse && <option value="integracao">Integração</option>}
              <option value="profile">Perfil</option>
            </select>
          </div>
          <nav className="nav-tabs">
            {isMaster && (
              <button 
                className={`nav-tab ${currentPage === 'dashboard' ? 'active' : ''}`}
                onClick={() => setCurrentPage('dashboard')}
              >
                <span className="nav-icon">⌂</span> Visão geral
              </button>
            )}
            {isSoftwareHouse && <button className={`nav-tab ${currentPage === 'clientes' ? 'active' : ''}`} onClick={() => setCurrentPage('clientes')}><span className="nav-icon">◇</span> Clientes</button>}
            {isMaster && <button className={`nav-tab ${currentPage === 'planos' ? 'active' : ''}`} onClick={() => setCurrentPage('planos')}><span className="nav-icon">$</span> Planos</button>}
            <button 
              className={`nav-tab ${currentPage === 'aplicativos' ? 'active' : ''}`}
              onClick={() => setCurrentPage('aplicativos')}
            >
              <span className="nav-icon">▣</span> Aplicativos
            </button>
            <button 
              className={`nav-tab ${currentPage === 'dados-carga' ? 'active' : ''}`}
              onClick={() => setCurrentPage('dados-carga')}
            >
              <span className="nav-icon">⇅</span> Carga de Dados
            </button>
            <button 
              className={`nav-tab ${currentPage === 'pedidos' ? 'active' : ''}`}
              onClick={() => setCurrentPage('pedidos')}
            >
              <span className="nav-icon">▤</span> Carga de Pedidos
            </button>
            <button
              className={`nav-tab ${currentPage === 'pagamentos' ? 'active' : ''}`}
              onClick={() => setCurrentPage('pagamentos')}
            >
              <span className="nav-icon">≡</span> Pedidos
            </button>
            <button 
              className={`nav-tab ${currentPage === 'faturas' ? 'active' : ''}`}
              onClick={() => setCurrentPage('faturas')}
            >
              <span className="nav-icon">$</span> Faturas
            </button>
            {isSoftwareHouse && <button className={`nav-tab ${currentPage === 'integracao' ? 'active' : ''}`} onClick={() => setCurrentPage('integracao')}><span className="nav-icon">↔</span> Integração</button>}
            <button 
              className={`nav-tab ${currentPage === 'profile' ? 'active' : ''}`}
              onClick={() => setCurrentPage('profile')}
            >
              <span className="nav-icon">○</span> Perfil
            </button>
          </nav>
        </div>

        <main className="page-container admin-main">
          {acessoLimitado && <div className="acesso-limitado-aviso" role="status">
            <strong>Acesso limitado</strong>
            <span>Consultas e pagamentos continuam disponíveis. Cadastros, ativações e liberações voltam após a regularização do pagamento.</span>
          </div>}
          {renderPage()}
        </main>
      </div>
    </AppContext.Provider>
  );
};

export default App;
